import React, { useState, useEffect } from 'react';
import { submitAssignment, getAssignmentSubmissions, getSubmittedAssignmentIds, getStudentNotifications, markNotificationAsRead, recordCourseDownload, uploadPaymentProof, getStudentPaymentProofs, PaymentProof, getGlobalSubmissionDeadline } from '../api';
import ClickSpark from './ClickSpark';
import {
  BookOpen,
  Award,
  FileText,
  Bell,
  User,
  LogOut,
  Home,
  ClipboardCheck,
  Settings,
  MessageSquare,
  Eye,
  CreditCard,
  GraduationCap,
  CheckCircle,
  AlertCircle,
  Clock,
  Calendar,
  BadgeCheck,
  Search,
  TrendingUp,
  ArrowUpRight,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Download,
  LayoutGrid,
  List
} from 'lucide-react';
import { EmailIcon, WhatsAppIcon } from './ContactIcons';

interface Course {
  id: number;
  title: string;
  instructor: string;
  progress: number;
  nextDeadline: string;
  status: string;
  color: string;
  description: string;
  credits: number;
  semester: string;
  lastActivity: string;
  assignmentsCount: number;
  completedAssignments: number;
  pdf?: string;
}

interface Assignment {
  id: number;
  title: string;
  course: string;
  courseId: number;
  dueDate: string;
  deadline?: string; // Date limite de soumission
  status: 'À rendre' | 'En cours' | 'Rendu' | 'Noté';
  priority: 'high' | 'medium' | 'low';
  description: string;
  type: 'Devoir' | 'TP' | 'Projet' | 'Examen';
  points: number;
  submittedAt?: string;
  grade?: number;
  maxGrade?: number;
  feedback?: string;
  semester?: string; // Semestre du cours (S1, S2, S3)
}

interface Result {
  id: number;
  title: string;
  course: string;
  courseId: number;
  score: number;
  maxScore: number;
  date: string;
  type: 'Devoir' | 'TP' | 'Projet' | 'Examen';
  weight: number; // Poids dans le calcul de la moyenne
  feedback?: string;
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
  subtitle: string;
  color: string;
}

interface CourseCardProps {
  course: Course;
}

interface AssignmentItemProps {
  assignment: Assignment;
}

interface ResultItemProps {
  result: Result;
}

interface StudentDashboardProps {
  studentName: string;
  studentInfo?: any; // Informations de l'étudiant depuis la table students
  onLogout: () => void;
}

interface StudentProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  matricule: string;
  yearOfStudy: string;
  studyYear: number; // Année d'étude numérique (1 ou 2)
  program: string;
  department: string;
  advisor: string;
  enrollmentDate: string;
  photoUrl?: string;
  address: string;
  emergencyContact: string;
  emergencyPhone: string;
}

interface DashboardSettings {
  theme: 'light' | 'dark';
  language: 'fr' | 'en';
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    showProfile: boolean;
    showProgress: boolean;
    allowContact: boolean;
  };
  accessibility: {
    fontSize: 'small' | 'medium' | 'large';
    highContrast: boolean;
    reduceMotion: boolean;
  };
}

// Interface pour les cours
interface CoursItem {
  nom: string;
  fichier: string;
  professeur?: string;
  // Surcharge l'accès du semestre pour ce cours précis.
  // Ex. un cours de S1 laissé ouvert aux 2ème année alors que le S1 leur est fermé.
  accessible_annee?: number[];
}

interface Notification {
  id: number;
  student_email: string;
  message_id: number;
  is_read: boolean;
  created_at: string;
  teacher_messages: {
    id: number;
    teacher_email: string;
    course_name: string;
    message_title: string;
    message_content: string;
    created_at: string;
  };
}

// Préférences par défaut du tableau de bord (sert aussi au bouton « Réinitialiser »)
const defaultSettings: DashboardSettings = {
  theme: 'light',
  language: 'fr',
  notifications: {
    email: true,
    push: true,
    sms: false
  },
  privacy: {
    showProfile: true,
    showProgress: true,
    allowContact: false
  },
  accessibility: {
    fontSize: 'medium',
    highContrast: false,
    reduceMotion: false
  }
};

// AJOUTER APRÈS LES IMPORTS
const coursParSemestre: { semestre: string; cours: CoursItem[]; accessible_annee: number[] }[] = [
  {
    semestre: 'Semestre 1',
    accessible_annee: [1], // Réservé à la 1ère année (voir surcharge par cours ci-dessous)
    cours: [
      { nom: "01 S1 PSYCHOPEDAGOGIE DE L'ENFANT ET DE L'ADOLESCENT", fichier: "01 S1 PSYCHOPEDAGOGIE DE L'ENFANT ET DE L'ADOLESCENT.pdf" },
      { nom: "02 S1 PSYCHOLOGIE DE L'APPRENTISSAGE", fichier: "02 S1 PSYCHOLOGIE DE L'APPRENTISSAGE.pdf" },
      { nom: "03 S1 Administration des etablissements eftp et gpec en eftp", fichier: "03 S1 Administration des etablissements eftp et gpec en eftp.pdf" },
      { nom: "04 S1 Etude des Textes Fondamentaux de l'EFTP", fichier: "04 S1 Etude des Textes Fondamentaux de l'EFTP.pdf" },
      { nom: "05 S1 GEOGRAPHIE DE L'EFTP", fichier: "05 S1 GEOGRAPHIE DE L'EFTP.pdf" },
      { nom: "06 S1 Analyse, Conception et Réalisation de Manuels Pédagogiques pour l'EFTP", fichier: "06 S1 Analyse, Conception et Réalisation de Manuels Pédagogiques pour l'EFTP.pdf", accessible_annee: [1, 2] },
      { nom: "07 S1 Théorie didactique", fichier: "07 S1 Théorie didactique.pdf" },
      { nom: "08 S1 Fondements de la Didactique des Disciplines de l'EFTP", fichier: "08 S1 Fondements de la Didactique des Disciplines de l EFTP.pdf" },
      { nom: "09 S1 Anglais Technique", fichier: "09 S1 Anglais Technique.pdf" },
      { nom: "10 S1 Communication scientifique en anglais", fichier: "10 S1 Communication scientifique en anglais.pdf" },
      { nom: "11 S1 Projet apprenant", fichier: "11 S1 Projet apprenant.pdf" }
    ]
  },
  {
    semestre: 'Semestre 2',
    accessible_annee: [1], // Réservé à la 1ère année
    cours: [
      { nom: "01 S2 Délinquance Juvénile", fichier: "01 S2 Délinquance Juvénile.pdf" },
      { nom: "02 S2 Epistomologie et science de l'education et de la formation", fichier: "02 S2 Epistomologie et science de l'education et de la formation.pdf" },
      { nom: "03 S2 Gestion de classes en situation formelle dans l'EFTP", fichier: "03 S2 Gestion de classes en situation formelle dans l'EFTP.pdf" },
      { nom: "04 S2 Gestion de classes de contexte de formation professionnelle", fichier: "04 S2 Gestion de classes de contexte de formation professionnelle.pdf" },
      { nom: "05 S2 Didactique de la matière en EFTP", fichier: "05 S2 Didactique de la matière en EFTP.pdf" },
      { nom: "06 S2 Docimologie", fichier: "06 S2 Docimologie.pdf" },
      { nom: "07 - 08 S2 Pedagogie et Andragogie", fichier: "07 - 08 S2 Pedagogie et Andragogie.pdf" },
      { nom: "09 S2 Sociologie de l'Education et Réalité de l'EFTP", fichier: "09 S2 Sociologie de l'Education et Réalité de l'EFTP.pdf" },
      { nom: "10 S2 Education des apprenants à besoin spécifiques", fichier: "10 S2 Education des apprenants à besoin spécifiques.pdf" },
      { nom: "11 S2 Ethique et déontologie de la profession enseignante", fichier: "11 S2 Ethique et déontologie de la profession enseignante.pdf" },
      { nom: "12 S2 Enseignement et formation en entreprise", fichier: "12 S2 Enseignement et formation en entreprise.pdf" }
    ]
  },
  {
    semestre: 'Semestre 3',
    accessible_annee: [2], // Accessible uniquement en 2ème année
    cours: [
      { nom: "01 S3 Collaboration interdisciplinaire dans l'EFTP", fichier: "01 S3 Collaboration interdisciplinaire dans l'EFTP.pdf" },
      { nom: "02 S3 Projet transverseaux dans l'EFTP", fichier: "02 S3 Projet transverseaux dans l'EFTP.pdf" },
      { nom: "03 S3 Conception et mise en oeuvre de projet de recherche action", fichier: "03 S3 Conception et mise en oeuvre de projet de recherche action.pdf" },
      { nom: "04 S3 Amélioration des pratiques pédagogiques dans les etablissements d'EFTP", fichier: "04 S3 Amélioration des pratiques pédagogiques dans les etablissements d'EFTP.pdf" },
      { nom: "05 S3 Appropriation des programmes d'études", fichier: "05 S3 Appropriation des programmes d'études.pdf" },
      { nom: "06 S3 Evaluation des programmes d'etude", fichier: "06 S3 Evaluation des programmes d'etude.pdf" },
      { nom: "07 - 08 S3 Conception et redaction des curricula dans l'EFTP", fichier: "07 - 08 S3 Conception et redaction des curricula dans l'EFTP.pdf" },
      { nom: "09 S3 Tice et innovation pédagogique en EFTP", fichier: "09 S3 Tice et innovation pédagogique en EFTP.pdf" },
      { nom: "10 S3 Anglais scientifique", fichier: "10 S3 Anglais scientifique.pdf" },
      { nom: "11 S3 Montage d'évènement scientifique et culturels", fichier: "11 S3 Montage d'évènement scientifique et culturels.pdf" }
    ]
  }
];

// Normalise le nom de fichier pour l'URL (apostrophe typographique Unicode → ASCII)
function normalizeFileNameForUrl(fileName: string): string {
  return fileName.replace(/\u2019/g, "'");
}

// Fonction de fabrique pour Assignment (réservée pour usage futur)
function _createAssignment(
  id: number,
  title: string,
  course: string,
  courseId: number,
  dueDate: string
): Assignment {
  return {
    id,
    title,
    course,
    courseId,
    dueDate,
    status: 'À rendre',
    priority: 'medium',
    description: `Rendez le devoir du cours ${course}.`,
    type: 'Devoir',
    points: 20
  };
}

export default function StudentDashboard({ studentName, studentInfo, onLogout }: StudentDashboardProps) {
  console.log('StudentDashboard - studentInfo:', studentInfo); // Debug
  console.log('StudentDashboard - studentName:', studentName); // Debug
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [watchedVideos, setWatchedVideos] = useState<Set<string>>(new Set());
  const [assignmentFilter, setAssignmentFilter] = useState<'all' | 'pending' | 'submitted' | 'graded'>('all');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const [savedPhone, setSavedPhone] = useState<string | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const [submissionFile, setSubmissionFile] = useState<File | null>(null);
  const [submissionTitle, setSubmissionTitle] = useState('');
  const [submissionComments, setSubmissionComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState<number | null>(null);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [submittedAssignmentIds, setSubmittedAssignmentIds] = useState<Set<number>>(new Set());
  const [globalSubmissionDeadline, setGlobalSubmissionDeadline] = useState<string | null>(null);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; name: string } | null>(null);
  const [scolariteProof, setScolariteProof] = useState<PaymentProof | null>(null);
  const [laboProof, setLaboProof] = useState<PaymentProof | null>(null);
  const [proofUploading, setProofUploading] = useState<'scolarite' | 'laboratoire' | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationDetail, setShowNotificationDetail] = useState<Notification | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  // Tableau de bord : recherche, filtre semestre, calendrier, rafraîchissement
  const [dashSearch, setDashSearch] = useState('');
  const [semesterFilter, setSemesterFilter] = useState<string>('all');
  const [calendarOffset, setCalendarOffset] = useState(0);
  const [isRefreshingDash, setIsRefreshingDash] = useState(false);
  const [coursesView, setCoursesView] = useState<'grid' | 'list'>('grid');
  const [profilePhone, setProfilePhone] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [assignmentsView, setAssignmentsView] = useState<'grid' | 'list'>('grid');

  // Un étudiant promu en 2ème année repart d'un compteur de devoirs à zéro.
  // Les dépôts déjà enregistrés au moment de la promotion sont mémorisés comme
  // référence et ne sont plus comptés ; les dépôts faits ensuite comptent normalement.
  // Rien n'est supprimé côté Supabase : c'est un décompte, pas une purge.
  const applyPromotionReset = (ids: number[]): Set<number> => {
    const studyYear = studentInfo?.study_year || 1;
    if (studyYear !== 2 || !studentInfo?.id) return new Set<number>(ids);

    const key = `promotion_baseline_${studentInfo.id}`;
    let baseline: number[] = [];
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        baseline = JSON.parse(saved);
      } else {
        baseline = ids;
        localStorage.setItem(key, JSON.stringify(ids));
      }
    } catch {
      baseline = [];
    }
    const baselineSet = new Set<number>(baseline);
    return new Set<number>(ids.filter(id => !baselineSet.has(id)));
  };

  // Charger les notifications au montage du composant
  useEffect(() => {
    const loadNotifications = async () => {
      try {
        const notificationsData = await getStudentNotifications(studentName);
        setNotifications(notificationsData || []);
        setUnreadCount(notificationsData?.filter(n => !n.is_read).length || 0);
      } catch (error) {
        console.error('Erreur lors du chargement des notifications:', error);
      }
    };

    loadNotifications();

    // Charger les devoirs soumis depuis Supabase (source de vérité, sans JOIN)
    if (studentInfo?.id) {
      getSubmittedAssignmentIds(studentInfo.id).then(ids => {
        if (ids.length > 0) {
          const idSet = applyPromotionReset(ids);
          setSubmittedAssignmentIds(idSet);
          localStorage.setItem(`submitted_assignments_${studentInfo.id}`, JSON.stringify(Array.from(idSet)));
        } else {
          // Fallback localStorage si Supabase ne retourne rien
          try {
            const saved = localStorage.getItem(`submitted_assignments_${studentInfo.id}`);
            if (saved) setSubmittedAssignmentIds(applyPromotionReset(JSON.parse(saved)));
          } catch {}
        }
      }).catch(() => {
        try {
          const saved = localStorage.getItem(`submitted_assignments_${studentInfo.id}`);
          if (saved) setSubmittedAssignmentIds(applyPromotionReset(JSON.parse(saved)));
        } catch {}
      });
    }

    // Charger photo et téléphone depuis localStorage
    if (studentInfo?.email) {
      const savedPhotoVal = localStorage.getItem(`profile_photo_${studentInfo.email}`);
      if (savedPhotoVal) setProfilePhoto(savedPhotoVal);
      const savedPhoneVal = localStorage.getItem(`profile_phone_${studentInfo.email}`);
      if (savedPhoneVal) setSavedPhone(savedPhoneVal);
      getStudentPaymentProofs(studentInfo.email).then(proofs => {
        setScolariteProof(proofs.find(p => p.type === 'scolarite') || null);
        setLaboProof(proofs.find(p => p.type === 'laboratoire') || null);
      }).catch(() => {});
    }

    // Debug: Afficher les informations de l'étudiant
    console.log('=== DASHBOARD ÉTUDIANT ===');
    console.log('Student Name:', studentName);
    console.log('Student Info:', studentInfo);
    console.log('Student ID:', studentInfo?.id);
    console.log('==========================');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- studentName suffit pour le chargement des notifications
  }, [studentName]);

  // Charger la deadline globale de soumission
  useEffect(() => {
    const loadGlobalDeadline = async () => {
      try {
        const deadline = await getGlobalSubmissionDeadline();
        setGlobalSubmissionDeadline(deadline);
      } catch (error) {
        console.error('Erreur lors du chargement de la deadline globale:', error);
      }
    };

    loadGlobalDeadline();
  }, []);

  // Appliquer le thème et les préférences au chargement
  React.useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    if (savedTheme) {
      setSettings(prev => ({ ...prev, theme: savedTheme }));
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }

    // Préférences enregistrées sur cet appareil
    try {
      const saved = localStorage.getItem('dashboard_settings');
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<DashboardSettings>;
        setSettings(prev => ({
          ...prev,
          ...parsed,
          theme: savedTheme || parsed.theme || prev.theme,
          privacy: { ...prev.privacy, ...(parsed.privacy || {}) },
          accessibility: { ...prev.accessibility, ...(parsed.accessibility || {}) },
          notifications: { ...prev.notifications, ...(parsed.notifications || {}) },
        }));
        const size = parsed.accessibility?.fontSize;
        if (size) {
          document.documentElement.style.fontSize =
            size === 'small' ? '14px' : size === 'large' ? '18px' : '16px';
        }
      }
    } catch (error) {
      console.error('Préférences illisibles, valeurs par défaut appliquées:', error);
    }
  }, []);
  const [settings, setSettings] = useState<DashboardSettings>(defaultSettings);

  // Données du profil étudiant - utiliser les vraies données de studentInfo
  const studentProfile: StudentProfile = {
    id: studentInfo?.id || 'STU001',
    firstName: studentInfo?.nom_complet ? studentInfo.nom_complet.split(' ')[0] : 'Prénom',
    lastName: studentInfo?.nom_complet ? studentInfo.nom_complet.split(' ').slice(1).join(' ') : 'Nom',
    email: studentInfo?.email || 'email@example.com',
    phone: savedPhone || studentInfo?.telephone || '+229 90 12 34 56',
    matricule: studentInfo?.matricule || 'Matricule',
    yearOfStudy: studentInfo?.niveau || '2ème année',
    studyYear: studentInfo?.study_year || 1, // Valeur par défaut à 1 si non définie
    program: studentInfo?.formation || 'Master MR-MRTDDEFTP',
    department: studentInfo?.departement || 'EFTP',
    advisor: studentInfo?.encadreur || 'Dr. GNONLONFOUN Jean Marc',
    enrollmentDate: studentInfo?.date_inscription || '2023-09-15',
    address: studentInfo?.adresse || '123 Rue de l\'Université, Cotonou, Bénin',
    emergencyContact: studentInfo?.contact_urgence || 'Contact d\'urgence',
    emergencyPhone: studentInfo?.telephone_urgence || '+229 90 98 76 54'
  };

  // Fonction pour filtrer les cours selon l'année d'étude
  // Un cours peut surcharger l'accès de son semestre via `accessible_annee`.
  const isCourseAccessible = (
    semestre: { accessible_annee: number[] },
    cours: CoursItem,
    studyYear: number
  ) => (cours.accessible_annee ?? semestre.accessible_annee).includes(studyYear);

  const getAccessibleCourses = () => {
    return coursParSemestre
      .map(semestre => ({
        ...semestre,
        cours: semestre.cours.filter(c => isCourseAccessible(semestre, c, studentProfile.studyYear)),
      }))
      .filter(semestre => semestre.cours.length > 0);
  };

  // Obtenir les cours accessibles à l'étudiant
  const accessibleCourses = getAccessibleCourses();
  // Données fictives - Tous les cours du semestre 3 (réservé pour usage futur)
  const _courses: Course[] = [
    {
      id: 1,
      title: "01 S3 Collaboration interdisciplinaire dans l'EFTP",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-08-15',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Développement de la collaboration entre disciplines dans l\'EFTP',
      credits: 4,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/01 S3 Collaboration interdisciplinaire dans l\'EFTP.pdf'
    },
    {
      id: 2,
      title: "02 S3 Projet transverseaux dans l'EFTP",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-08-20',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Conception et mise en œuvre de projets transversaux',
      credits: 5,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/02 S3 Projet transverseaux dans l\'EFTP.pdf'
    },
    {
      id: 3,
      title: "03 S3 Conception et mise en oeuvre de projet de recherche action",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-08-25',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Méthodologie de recherche-action en contexte EFTP',
      credits: 6,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/03 S3 Conception et mise en oeuvre de projet de recherche action.pdf'
    },
    {
      id: 4,
      title: "04 S3 Amélioration des pratiques pédagogiques dans les etablissements d'EFTP",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-08-30',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Amélioration des pratiques pédagogiques en EFTP',
      credits: 4,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/04 S3 Amélioration des pratiques pédagogiques dans les etablissements d\'EFTP.pdf'
    },
    {
      id: 5,
      title: "05 S3 Appropriation des programmes d'études",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-05',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Appropriation et mise en œuvre des programmes d\'études',
      credits: 5,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/05 S3 Appropriation des programmes d\'études.pdf'
    },
    {
      id: 6,
      title: "06 S3 Evaluation des programmes d'etude",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-10',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Évaluation et amélioration des programmes d\'études',
      credits: 4,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/06 S3 Evaluation des programmes d\'etude.pdf'
    },
    {
      id: 7,
      title: "07 - 08 S3 Conception et redaction des curricula dans l'EFTP",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-15',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Conception et rédaction des curricula en EFTP',
      credits: 6,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/07 - 08 S3 Conception et redaction des curricula dans l\'EFTP.pdf'
    },
    {
      id: 8,
      title: "09 S3 Tice et innovation pédagogique en EFTP",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-20',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Technologies de l\'information et innovation pédagogique',
      credits: 4,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/09 S3 Tice et innovation pédagogique en EFTP.pdf'
    },
    {
      id: 9,
      title: "10 S3 Anglais scientifique",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-25',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Anglais scientifique et communication académique',
      credits: 3,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/10 S3 Anglais scientifique.pdf'
    },
    {
      id: 10,
      title: "11 S3 Montage d'évènement scientifique et culturels",
      instructor: '',
      progress: 0,
      nextDeadline: '2025-09-30',
      status: 'Non commencé',
      color: 'bg-blue-500',
      description: 'Organisation d\'événements scientifiques et culturels',
      credits: 4,
      semester: 'Semestre 3',
      lastActivity: 'Aucune activité',
      assignmentsCount: 0,
      completedAssignments: 0,
      pdf: '/cours/semestre3/11 S3 Montage d\'évènement scientifique et culturels.pdf'
    }
  ];

  // Fonction pour générer tous les devoirs basés sur les cours accessibles
  const generateAllAssignments = (): Assignment[] => {
    const allAssignments: Assignment[] = [];
    let assignmentId = 1;

    coursParSemestre.forEach(semestre => {
      semestre.cours.forEach(cours => {
        allAssignments.push({
          id: assignmentId++,
          title: `Devoir - ${cours.nom}`,
          course: cours.nom,
          courseId: assignmentId - 1,
          dueDate: '',
          status: 'À rendre',
          priority: 'medium',
          description: `Rendez le devoir du cours ${cours.nom}`,
          type: 'Devoir',
          points: 20,
          semester: semestre.semestre
        });
      });
    });

    return allAssignments;
  };

  // Générer tous les devoirs
  const allAssignments = generateAllAssignments();

  const recentResults: Result[] = [];

  // Données fictives pour les supports de cours
  const courseSupports: any = {};

  const handleViewCourse = (course: Course) => {
    setSelectedCourse(course);
  };

  const handleBackToDashboard = () => {
    setSelectedCourse(null);
  };

  const handleVideoComplete = (courseId: number, videoId: number) => {
    const videoKey = `${courseId}-${videoId}`;
    setWatchedVideos(prev => new Set(Array.from(prev).concat([videoKey])));
  };

  const isVideoUnlocked = (courseId: number, videoIndex: number) => {
    if (videoIndex === 0) return true; // Première vidéo toujours accessible
    
    // Vérifier si la vidéo précédente a été regardée
    const previousVideoId = videoIndex;
    const previousVideoKey = `${courseId}-${previousVideoId}`;
    const previousVideoWatched = watchedVideos.has(previousVideoKey);
    
    return previousVideoWatched;
  };

  const isVideoWatched = (courseId: number, videoId: number) => {
    const videoKey = `${courseId}-${videoId}`;
    return watchedVideos.has(videoKey);
  };

  // Fonction pour obtenir les devoirs accessibles selon l'année d'étude
  const getAccessibleAssignments = () => {
    return allAssignments.filter(assignment => {
      // Retrouver le semestre et le cours d'origine
      const semestre = coursParSemestre.find(s =>
        s.cours.some(c => c.nom === assignment.course)
      );
      if (!semestre) return false;

      const cours = semestre.cours.find(c => c.nom === assignment.course);
      if (!cours) return false;

      // L'accès se décide au niveau du cours, pas du semestre
      return isCourseAccessible(semestre, cours, studentProfile.studyYear);
    });
  };

  const getFilteredAssignments = () => {
    const accessibleAssignments = getAccessibleAssignments();
    
    switch (assignmentFilter) {
      case 'pending':
        return accessibleAssignments.filter(a => !submittedAssignmentIds.has(a.id));
      case 'submitted':
        return accessibleAssignments.filter(a => submittedAssignmentIds.has(a.id));
      case 'graded':
        return accessibleAssignments.filter(a => a.status === 'Noté');
      default:
        return accessibleAssignments;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'À rendre':
        return 'bg-red-100 text-red-700';
      case 'En cours':
        return 'bg-orange-100 text-orange-700';
      case 'Rendu':
        return 'bg-blue-100 text-blue-700';
      case 'Noté':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-green-500';
      default:
        return 'bg-slate-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Devoir':
        return <FileText className="w-4 h-4" />;
      case 'TP':
        return <ClipboardCheck className="w-4 h-4" />;
      case 'Projet':
        return <BookOpen className="w-4 h-4" />;
      case 'Examen':
        return <Award className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const menuItems = [
    { id: 'dashboard',  label: 'Tableau de bord', icon: Home },
    { id: 'courses',    label: 'Mes cours',        icon: BookOpen },
    { id: 'assignments',label: 'Devoirs',           icon: ClipboardCheck },
    { id: 'scolarite',  label: 'Ma scolarité',      icon: CreditCard },
    { id: 'profile',    label: 'Profil',            icon: User },
    { id: 'settings',   label: 'Paramètres',        icon: Settings },
  ];

  // Sidebar : navigation principale en haut, secondaire en bas
  const mainMenuItems = menuItems.filter(i => !['profile', 'settings'].includes(i.id));
  const bottomMenuItems = menuItems.filter(i => ['profile', 'settings'].includes(i.id));

  const _StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value, subtitle, color }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 hover:shadow-md hover:border-blue-100 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold tracking-wider text-slate-400 uppercase mb-2">{title}</p>
          <p className="text-2xl font-bold text-slate-900 tracking-tight">{value}</p>
          <p className="text-xs text-slate-400 mt-1">{subtitle}</p>
        </div>
        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center flex-shrink-0 ml-3`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  const _CourseCard: React.FC<CourseCardProps> = ({ course }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{course.title}</h3>
          {course.instructor && (
          <p className="text-sm text-slate-600 dark:text-slate-400">Prof. {course.instructor}</p>
          )}
        </div>
        <span className={`w-3 h-3 rounded-full ${course.color}`}></span>
      </div>



      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-500 dark:text-slate-400">Échéance: {course.nextDeadline}</span>
        <div className="flex space-x-2">
          <a
            className="text-xs font-medium px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95"
            href={course.pdf}
            download
            target="_blank"
            rel="noopener noreferrer"
          >
            Télécharger
          </a>
        </div>
      </div>
    </div>
  );

  const _DetailedCourseCard: React.FC<{ course: Course }> = ({ course }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className={`w-3 h-3 rounded-full ${course.color}`}></span>
            <span className={`text-xs px-2 py-1 rounded-full ${
              course.status === 'Presque terminé' ? 'bg-green-100 text-green-700' :
              course.status === 'En cours' ? 'bg-blue-100 text-blue-700' :
              'bg-orange-100 text-orange-700'
            }`}>
              {course.status}
            </span>
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1 text-lg">{course.title}</h3>
          {course.instructor && (
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Prof. {course.instructor}</p>
          )}
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{course.description}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Crédits</p>
          <p className="font-medium text-slate-900 dark:text-white">{course.credits} ECTS</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Semestre</p>
          <p className="font-medium text-slate-900 dark:text-white">{course.semester}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Devoirs</p>
          <p className="font-medium text-slate-900 dark:text-white">{course.completedAssignments}/{course.assignmentsCount}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Dernière activité</p>
          <p className="font-medium text-slate-900 dark:text-white">{course.lastActivity}</p>
        </div>
      </div>
      

      
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Prochaine échéance</p>
          <p className="text-sm font-medium text-slate-900 dark:text-white">{course.nextDeadline}</p>
        </div>
        <div className="flex space-x-2">
          <ClickSpark sparkColor="#ffffff" sparkSize={6} sparkRadius={15} sparkCount={8}>
            <button 
              onClick={() => handleViewCourse(course)}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95"
            >
              Voir le cours
            </button>
          </ClickSpark>
          <ClickSpark sparkColor="#ffffff" sparkSize={6} sparkRadius={15} sparkCount={8}>
            <button
              onClick={() => course.pdf && handleCourseDownload(course.title, course.pdf)}
              className="text-sm font-medium px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm hover:shadow-md transform hover:scale-105 active:scale-95"
            >
              Télécharger
            </button>
          </ClickSpark>
        </div>
      </div>
      {course.pdf && (
        <button
          onClick={() => handleCourseDownload(course.title, course.pdf!)}
          className="inline-block px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors mt-4"
        >
          Télécharger le PDF du cours
        </button>
      )}
    </div>
  );

  const CourseDetail: React.FC<{ course: Course; onBack: () => void }> = ({ course, onBack }) => {
    const supports = courseSupports[course.id as keyof typeof courseSupports];
    const [selectedVideo, setSelectedVideo] = useState<number | null>(null);
    
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{course.title}</h1>
              {course.instructor && (
              <p className="text-slate-600">Prof. {course.instructor}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              course.status === 'Presque terminé' ? 'bg-green-100 text-green-700' :
              course.status === 'En cours' ? 'bg-blue-100 text-blue-700' :
              'bg-orange-100 text-orange-700'
            }`}>
              {course.status}
            </span>
          </div>
        </div>

        {/* Course Info */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Informations du cours</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Crédits:</span>
                  <span className="font-medium dark:text-white">{course.credits} ECTS</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Semestre:</span>
                  <span className="font-medium dark:text-white">{course.semester}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Progression:</span>
                  <span className="font-medium dark:text-white">{course.progress}%</span>
                </div>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Description</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm">{course.description}</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Prochaine échéance</h3>
              <p className="text-slate-600 dark:text-slate-400">{course.nextDeadline}</p>
            </div>
          </div>
        </div>

        {/* Videos */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-4">Vidéos de cours</h3>
          
          {/* Video Player */}
          {selectedVideo && supports.videos && supports.videos[selectedVideo - 1] && (
            <div className="mb-6">
              <div className="relative w-full bg-black rounded-lg overflow-hidden">
                <video 
                  className="w-full h-auto max-h-96"
                  controls
                  preload="metadata"
                  onEnded={() => handleVideoComplete(course.id, selectedVideo)}
                >
                  <source src={supports.videos[selectedVideo - 1].url} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <h4 className="font-medium text-slate-900 dark:text-white">{supports.videos[selectedVideo - 1].title}</h4>
                <button 
                  onClick={() => setSelectedVideo(null)}
                  className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
            {supports.videos && supports.videos.map((video: any, index: number) => {
              const isUnlocked = isVideoUnlocked(course.id, index);
              const isWatched = isVideoWatched(course.id, video.id);
              
              return (
                <div key={video.id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  isUnlocked 
                    ? 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700' 
                    : 'border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                }`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      isWatched ? 'bg-green-500' : 
                      isUnlocked ? 'bg-slate-300 dark:bg-slate-500' : 'bg-slate-200 dark:bg-slate-600'
                    }`}></div>
                    <div>
                      <p className={`font-medium ${
                        isUnlocked ? 'text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'
                      }`}>
                        {video.title}
                        {!isUnlocked && (
                          <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">(Vidéo précédente requise)</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-600 dark:text-slate-400">{video.duration}</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => isUnlocked && setSelectedVideo(video.id)}
                    disabled={!isUnlocked}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      isUnlocked
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {isUnlocked ? 'Regarder' : 'Vidéo précédente requise'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Documents de cours</h3>
          <div className="space-y-3">
            {supports.documents && supports.documents.map((doc: any) => (
              <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center">
                    <span className="text-xs font-medium text-slate-600">{doc.type}</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{doc.title}</p>
                    <p className="text-sm text-slate-600">{doc.size}</p>
                  </div>
                </div>
                <ClickSpark sparkColor="#ffffff" sparkSize={6} sparkRadius={15} sparkCount={8}>
                  <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                    Télécharger
                  </button>
                </ClickSpark>
              </div>
            ))}
          </div>
        </div>

        {/* Assignments */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Devoirs</h3>
          <div className="space-y-3">
            {supports.assignments && supports.assignments.map((assignment: any) => (
              <div key={assignment.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-medium text-slate-900">{assignment.title}</p>
                  <p className="text-sm text-slate-600">Échéance: {assignment.dueDate}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    assignment.status === 'Terminé' ? 'bg-green-100 text-green-700' :
                    assignment.status === 'En cours' ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {assignment.status}
                  </span>
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    Voir
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Practical Works */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200">
          <h3 className="font-semibold text-slate-900 mb-4">Travaux Pratiques</h3>
          <div className="space-y-3">
            {supports.practicalWorks && supports.practicalWorks.map((tp: any) => (
              <div key={tp.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
                <div>
                  <p className="font-medium text-slate-900">{tp.title}</p>
                  <p className="text-sm text-slate-600">Échéance: {tp.dueDate}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    tp.status === 'Terminé' ? 'bg-green-100 text-green-700' :
                    tp.status === 'En cours' ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {tp.status}
                  </span>
                  <ClickSpark sparkColor="#ffffff" sparkSize={6} sparkRadius={15} sparkCount={8}>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
                      Commencer
                    </button>
                  </ClickSpark>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const AssignmentCard: React.FC<{ assignment: Assignment }> = ({ assignment }) => {
    const isSubmitted = submittedAssignmentIds.has(assignment.id);
    const deadlinePassed = getGlobalDeadlineStatus().isPast;
    const locked = isSubmitted || deadlinePassed;
    return (
      <div className={`bg-white dark:bg-slate-800 rounded-3xl p-5 border shadow-sm transition-all duration-200 flex flex-col ${
        isSubmitted
          ? 'border-green-100 dark:border-green-500/20'
          : 'border-slate-200 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-500/40 hover:shadow-md'
      }`}>
        <div className="flex items-start justify-between mb-4">
          <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${
            isSubmitted ? 'bg-green-50 dark:bg-green-500/10' : 'bg-blue-50 dark:bg-blue-500/10'
          }`}>
            <FileText className={`w-4 h-4 ${isSubmitted ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
            isSubmitted
              ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
              : 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400'
          }`}>
            {isSubmitted ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
            {isSubmitted ? 'Rendu' : 'À rendre'}
          </span>
        </div>

        <div className="flex-1 mb-4">
          <p className="text-xs text-slate-400 mb-1">{assignment.semester || 'Devoir'} · {assignment.points} pts</p>
          <h3 className="font-medium text-slate-900 dark:text-white text-sm leading-snug line-clamp-2">{assignment.course}</h3>
        </div>

        <button
          onClick={() => { if (!locked) handleSubmitAssignment(assignment); }}
          disabled={locked}
          className={`w-full px-4 py-2.5 rounded-xl transition-colors text-sm font-medium ${
            locked
              ? 'bg-slate-100 text-slate-400 cursor-default dark:bg-slate-700 dark:text-slate-500'
              : 'bg-slate-900 dark:bg-blue-600 text-white hover:bg-slate-800 dark:hover:bg-blue-700'
          }`}
        >
          {isSubmitted ? 'Devoir remis' : deadlinePassed ? 'Délai dépassé' : 'Rendre le devoir'}
        </button>
      </div>
    );
  };

  const _AssignmentItem: React.FC<AssignmentItemProps> = ({ assignment }) => (
    <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-sm transition-all duration-200">
      <div className="flex items-center space-x-3">
        <div className={`w-2 h-2 rounded-full ${getPriorityColor(assignment.priority)}`}></div>
        <div>
          <h4 className="font-medium text-slate-900 dark:text-white">{assignment.title}</h4>
          <p className="text-sm text-slate-600 dark:text-slate-400">{assignment.course}</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm text-slate-900 dark:text-white">{assignment.dueDate}</p>
        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(assignment.status)}`}>
          {assignment.status}
        </span>
      </div>
    </div>
  );

  const _calculateAverage = () => {
    if (recentResults.length === 0) return 0;
    
    let totalWeightedScore = 0;
    let totalWeight = 0;
    
    recentResults.forEach(result => {
      const percentage = (result.score / result.maxScore) * 20; // Convertir sur 20
      totalWeightedScore += percentage * result.weight;
      totalWeight += result.weight;
    });
    
    return totalWeight > 0 ? totalWeightedScore / totalWeight : 0;
  };

  const getGradeColor = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const getGradeEmoji = (score: number, maxScore: number) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return '🎉';
    if (percentage >= 60) return '👍';
    return '📝';
  };

  const _ResultCard: React.FC<{ result: Result }> = ({ result }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <div className="flex items-center space-x-1 text-slate-500 dark:text-slate-400">
              {getTypeIcon(result.type)}
              <span className="text-xs">{result.type}</span>
            </div>
            <span className="text-xs text-slate-500 dark:text-slate-400">{result.course}</span>
          </div>
          <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{result.title}</h3>
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">Rendu le {result.date}</p>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${getGradeColor(result.score, result.maxScore)}`}>
            {result.score}/{result.maxScore}
          </div>
          <div className="text-sm text-slate-500 dark:text-slate-400">
            {Math.round((result.score / result.maxScore) * 100)}%
          </div>
          <div className="text-lg">{getGradeEmoji(result.score, result.maxScore)}</div>
        </div>
      </div>

      {result.feedback && (
        <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Feedback</p>
          <p className="text-sm text-slate-700 dark:text-slate-300">{result.feedback}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Poids: {result.weight}x</span>
        </div>
        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
          Voir détails
        </button>
      </div>
    </div>
  );

  const _ResultItem: React.FC<ResultItemProps> = ({ result }) => (
    <div className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200">
      <div>
        <h4 className="font-medium text-slate-900">{result.title}</h4>
        <p className="text-sm text-slate-600">{result.course}</p>
        <p className="text-xs text-slate-500">{result.date}</p>
      </div>
      <div className="text-right">
        <p className={`text-lg font-bold ${getGradeColor(result.score, result.maxScore)}`}>
          {result.score}/{result.maxScore}
        </p>
        <p className="text-sm text-slate-600">{Math.round((result.score / result.maxScore) * 100)}%</p>
      </div>
    </div>
  );

  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        setProfilePhoto(dataUrl);
        if (studentInfo?.email) {
          localStorage.setItem(`profile_photo_${studentInfo.email}`, dataUrl);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const getGlobalDeadlineStatus = () => {
    if (!globalSubmissionDeadline) return { isPast: false, isApproaching: false, hoursLeft: null };
    const now = new Date();
    const deadlineDate = new Date(globalSubmissionDeadline);
    const hoursLeft = (deadlineDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return {
      isPast: now > deadlineDate,
      isApproaching: hoursLeft > 0 && hoursLeft <= 24,
      hoursLeft: hoursLeft > 0 ? Math.ceil(hoursLeft) : null,
      deadline: globalSubmissionDeadline
    };
  };

  const handleSubmitAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setSubmissionTitle(`Devoir - ${assignment.course}`);
    setIsSubmissionModalOpen(true);
    setSubmissionError('');
    setSubmissionSuccess(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const isZip =
      file.type === 'application/zip' ||
      file.type === 'application/x-zip-compressed' ||
      file.type === 'application/x-zip' ||
      file.name.toLowerCase().endsWith('.zip');

    if (isZip) {
      setSubmissionFile(file);
      setSubmissionError('');
    } else {
      setSubmissionError('Format invalide. Compressez tous vos fichiers en un seul archive .zip avant de soumettre.');
      setSubmissionFile(null);
    }
  };

  const handleSubmissionSubmit = async () => {
    // Debug: Afficher les informations de débogage
    console.log('=== DEBUG SOUMISSION ===');
    console.log('Student Info:', studentInfo);
    console.log('Student ID:', studentInfo?.id);
    console.log('Selected Assignment:', selectedAssignment);
    console.log('Submission File:', submissionFile);
    console.log('Submission Title:', submissionTitle);
    console.log('=======================');

    if (!submissionFile || !selectedAssignment || !studentInfo?.id) {
      setSubmissionError('Veuillez sélectionner un fichier à soumettre.');
      return;
    }

    if (!submissionTitle.trim()) {
      setSubmissionError('Veuillez saisir le titre du devoir.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setUploadSpeed(null);
    setSubmissionError('');

    try {
      await submitAssignment(
        selectedAssignment.id,
        studentInfo.id,
        submissionFile,
        submissionTitle,
        submissionComments,
        (percent: number, speed?: number) => {
          setUploadProgress(percent);
          if (speed !== undefined) setUploadSpeed(speed);
        }
      );

      setSubmissionSuccess(true);
      setSubmissionFile(null);
      setSubmissionTitle('');
      setSubmissionComments('');

      // Marquer le devoir comme rendu
      const newSubmitted = new Set(submittedAssignmentIds);
      newSubmitted.add(selectedAssignment.id);
      setSubmittedAssignmentIds(newSubmitted);
      if (studentInfo?.id) {
        localStorage.setItem(`submitted_assignments_${studentInfo.id}`, JSON.stringify(Array.from(newSubmitted)));
      }

      // Fermer le modal après 2 secondes
      setTimeout(() => {
        setIsSubmissionModalOpen(false);
        setSubmissionSuccess(false);
        setSelectedAssignment(null);
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors de la soumission. Veuillez réessayer.';
      setSubmissionError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSubmissionModal = () => {
    if (isSubmitting) return;
    setIsSubmissionModalOpen(false);
    setSelectedAssignment(null);
    setSubmissionFile(null);
    setSubmissionTitle('');
    setSubmissionComments('');
    setSubmissionError('');
    setSubmissionSuccess(false);
    setUploadProgress(0);
    setUploadSpeed(null);
  };

  // Fonction pour gérer le téléchargement d'un cours
  const handleCourseDownload = async (courseName: string, pdfPath: string) => {
    try {
      // Encoder le nom de fichier (espaces, apostrophes, virgules) pour une URL valide
      const lastSlash = pdfPath.lastIndexOf('/');
      const basePath = lastSlash >= 0 ? pdfPath.slice(0, lastSlash + 1) : '/cours/';
      const fileName = lastSlash >= 0 ? pdfPath.slice(lastSlash + 1) : pdfPath;
      const encodedPath = basePath + encodeURIComponent(normalizeFileNameForUrl(fileName));
      
      // Vérifier d'abord si le fichier existe
      const response = await fetch(encodedPath, { method: 'HEAD' });
      
      if (!response.ok) {
        throw new Error(`Fichier non trouvé: ${courseName}`);
      }
      
      // Enregistrer le téléchargement dans la base de données
      await recordCourseDownload(studentName, courseName);
      
      // Télécharger le fichier
      const link = document.createElement('a');
      link.href = encodedPath;
      link.download = courseName + '.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur lors du téléchargement';
      alert(`Erreur: ${errorMessage}`);
    }
  };

  // Fonction pour marquer une notification comme lue
  const handleMarkNotificationAsRead = async (notificationId: number) => {
    try {
      await markNotificationAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erreur lors du marquage de la notification:', error);
    }
  };

  // Fonction pour afficher le détail d'une notification
  const handleViewNotification = (notification: Notification) => {
    if (!notification.is_read) {
      handleMarkNotificationAsRead(notification.id);
    }
    setShowNotificationDetail(notification);
  };

  const ProfileSection: React.FC = () => {
    // Champs réellement modifiables : téléphone et photo. Le reste vient du
    // dossier étudiant et n'a pas d'API de mise à jour côté serveur.
    const identityRows = [
      { label: 'Prénom', value: studentProfile.firstName },
      { label: 'Nom', value: studentProfile.lastName },
      { label: 'Email', value: studentProfile.email },
    ];

    const academicRows = [
      { label: 'Matricule', value: studentProfile.matricule, mono: true },
      { label: 'Formation', value: studentProfile.program },
      { label: 'Département', value: studentProfile.department },
      { label: 'Encadreur', value: studentProfile.advisor },
    ];

    return (
      <div className="space-y-4 sm:space-y-5">

        {/* ── Actions ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
            <BadgeCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
              {studentProfile.studyYear === 1 ? '1ère année' : '2ème année'} · Master MR-MRTDDEFTP
            </span>
          </div>

          <div className="flex items-center gap-2">
            {!isEditingProfile ? (
              <button
                onClick={() => { setProfilePhone(savedPhone || studentProfile.phone || ''); setIsEditingProfile(true); }}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 dark:bg-blue-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Settings className="w-4 h-4" />
                Modifier
              </button>
            ) : (
              <>
                <button
                  onClick={() => { setProfilePhone(savedPhone || studentProfile.phone); setIsEditingProfile(false); }}
                  className="inline-flex items-center h-11 px-5 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveProfile}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 dark:bg-blue-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors shadow-sm"
                >
                  <CheckCircle className="w-4 h-4" />
                  Enregistrer
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* ── Carte identité ── */}
          <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-6 shadow-sm flex flex-col items-center text-center">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center overflow-hidden mb-4">
                {profilePhoto ? (
                  <img src={profilePhoto} alt="Profil" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-3xl font-bold text-white">
                    {(studentInfo?.nom_complet || studentName).charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              {isEditingProfile && (
                <label className="absolute bottom-3 -right-1 w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-700 flex items-center justify-center cursor-pointer transition-colors border-4 border-slate-900 dark:border-slate-800">
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                  <Settings className="w-3.5 h-3.5 text-white" />
                </label>
              )}
            </div>

            <h2 className="text-lg font-semibold text-white tracking-tight leading-tight">
              {studentInfo?.nom_complet || studentName}
            </h2>
            <p className="text-sm text-slate-400 mt-1 break-all">{studentProfile.email}</p>

            <div className="w-full border-t border-white/10 mt-5 pt-5 grid grid-cols-2 gap-3">
              <div>
                <p className="text-[22px] leading-none font-bold text-white tabular-nums">{dashTotalCourses}</p>
                <p className="text-xs text-slate-400 mt-1.5">Cours</p>
              </div>
              <div>
                <p className="text-[22px] leading-none font-bold text-white tabular-nums">{dashProgress}%</p>
                <p className="text-xs text-slate-400 mt-1.5">Devoirs rendus</p>
              </div>
            </div>
          </div>

          {/* ── Informations ── */}
          <div className="lg:col-span-2 space-y-4">

            {/* Informations personnelles */}
            <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Informations personnelles</h3>
              <p className="text-xs text-slate-400 mb-5">Seul le téléphone est modifiable ici.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {identityRows.map(({ label, value }) => (
                  <div key={label} className="px-4 py-3 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700">
                    <p className="text-xs text-slate-400 mb-1">{label}</p>
                    <p className="text-sm font-medium text-slate-900 dark:text-white break-words">{value || '—'}</p>
                  </div>
                ))}

                {/* Téléphone — modifiable */}
                <div className={`px-4 py-3 rounded-2xl border transition-colors ${
                  isEditingProfile
                    ? 'bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-500/40'
                    : 'bg-slate-50/70 dark:bg-slate-700/30 border-slate-100 dark:border-slate-700'
                }`}>
                  <p className="text-xs text-slate-400 mb-1">Téléphone</p>
                  {isEditingProfile ? (
                    <input
                      type="tel"
                      value={profilePhone}
                      onChange={(e) => setProfilePhone(e.target.value)}
                      placeholder="+229 ..."
                      className="w-full bg-transparent text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
                    />
                  ) : (
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {savedPhone || studentProfile.phone || '—'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Informations académiques */}
            <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Informations académiques</h3>
              <p className="text-xs text-slate-400 mb-5">Gérées par l'administration.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="px-4 py-3 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700">
                  <p className="text-xs text-slate-400 mb-1">Année d'étude</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    studentProfile.studyYear === 1
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
                      : 'bg-purple-50 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400'
                  }`}>
                    {studentProfile.studyYear === 1 ? '1ère année' : '2ème année'}
                  </span>
                </div>

                {academicRows.map(({ label, value, mono }) => (
                  <div key={label} className="px-4 py-3 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700">
                    <p className="text-xs text-slate-400 mb-1">{label}</p>
                    <p className={`text-sm font-medium text-slate-900 dark:text-white break-words ${mono ? 'font-mono' : ''}`}>
                      {value || '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSettingChange = (category: keyof DashboardSettings, field: string, value: any) => {
    setSettings(prev => {
      const newSettings = { ...prev };
      if (category === 'notifications') {
        newSettings.notifications = { ...prev.notifications, [field]: value };
      } else if (category === 'privacy') {
        newSettings.privacy = { ...prev.privacy, [field]: value };
      } else if (category === 'accessibility') {
        newSettings.accessibility = { ...prev.accessibility, [field]: value };
      }
      return newSettings;
    });
  };

  const handleThemeChange = (theme: 'light' | 'dark') => {
    setSettings(prev => ({
      ...prev,
      theme
    }));
    // Appliquer le thème au niveau global
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // ── Profil : seul le téléphone est persisté (pas d'API de mise à jour étudiant) ──
  const handleSaveProfile = () => {
    if (studentInfo?.email) {
      localStorage.setItem(`profile_phone_${studentInfo.email}`, profilePhone);
      setSavedPhone(profilePhone);
    }
    setIsEditingProfile(false);
  };

  // ── Paramètres : persistance locale ──
  const SETTINGS_KEY = 'dashboard_settings';

  const applyFontSize = (size: 'small' | 'medium' | 'large') => {
    const scale = size === 'small' ? '14px' : size === 'large' ? '18px' : '16px';
    document.documentElement.style.fontSize = scale;
  };

  const handleFontSizeChange = (size: 'small' | 'medium' | 'large') => {
    setSettings(prev => ({ ...prev, accessibility: { ...prev.accessibility, fontSize: size } }));
    applyFontSize(size);
  };

  const handleSaveSettings = () => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 2000);
    } catch (error) {
      console.error('Impossible d’enregistrer les paramètres:', error);
    }
  };

  const handleResetSettings = () => {
    setSettings(defaultSettings);
    applyFontSize(defaultSettings.accessibility.fontSize);
    handleThemeChange(defaultSettings.theme);
    try {
      localStorage.removeItem(SETTINGS_KEY);
    } catch {}
  };

  // ── Export des données de l'étudiant ──
  const handleExportData = () => {
    const payload = {
      exporte_le: new Date().toISOString(),
      profil: {
        nom_complet: studentInfo?.nom_complet || studentName,
        email: studentProfile.email,
        matricule: studentProfile.matricule,
        telephone: savedPhone || studentProfile.phone,
        annee_etude: studentProfile.studyYear,
        formation: studentProfile.program,
        departement: studentProfile.department,
        annee_academique: studentInfo?.annee_academique || null,
      },
      cours: accessibleCourses.map(s => ({
        semestre: s.semestre,
        cours: s.cours.map(c => c.nom),
      })),
      devoirs: getAccessibleAssignments().map(a => ({
        cours: a.course,
        semestre: a.semester,
        points: a.points,
        rendu: submittedAssignmentIds.has(a.id),
      })),
      preuves_paiement: {
        scolarite: scolariteProof ? scolariteProof.uploaded_at : null,
        laboratoire: laboProof ? laboProof.uploaded_at : null,
      },
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `mes-donnees-${studentProfile.matricule || 'etudiant'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const SettingsSection: React.FC = () => {
    const privacyRows: { field: 'showProfile' | 'showProgress' | 'allowContact'; label: string; hint: string }[] = [
      { field: 'showProfile',  label: 'Profil public',        hint: 'Permettre aux autres de voir votre profil' },
      { field: 'showProgress', label: 'Progression visible',  hint: 'Afficher votre progression aux autres' },
      { field: 'allowContact', label: 'Autoriser les contacts', hint: 'Permettre aux autres de vous contacter' },
    ];

    return (
      <div className="space-y-4 sm:space-y-5">

        {/* ── Actions ── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
            <Settings className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
              Préférences enregistrées sur cet appareil
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleResetSettings}
              className="inline-flex items-center h-11 px-5 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
            >
              Réinitialiser
            </button>
            <button
              onClick={handleSaveSettings}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-slate-900 dark:bg-blue-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors shadow-sm"
            >
              <CheckCircle className="w-4 h-4" />
              {settingsSaved ? 'Enregistré' : 'Enregistrer'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* ── Apparence ── */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Apparence</h3>
            <p className="text-xs text-slate-400 mb-5">Thème et lisibilité du tableau de bord.</p>

            <div className="space-y-5">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2.5">Thème</p>
                <div className="grid grid-cols-2 gap-3">
                  {([
                    { id: 'light' as const, label: 'Clair', dot: 'bg-amber-400' },
                    { id: 'dark'  as const, label: 'Sombre', dot: 'bg-slate-700 dark:bg-slate-400' },
                  ]).map(({ id, label, dot }) => (
                    <button
                      key={id}
                      onClick={() => handleThemeChange(id)}
                      className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border transition-all duration-200 ${
                        settings.theme === id
                          ? 'border-slate-900 dark:border-blue-500 bg-slate-50 dark:bg-slate-700/50'
                          : 'border-slate-100 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-700/30 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <span className={`w-3.5 h-3.5 rounded-full flex-shrink-0 ${dot}`} />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</span>
                      {settings.theme === id && <CheckCircle className="w-4 h-4 text-slate-900 dark:text-blue-400 ml-auto flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2.5">Taille de police</p>
                <div className="inline-flex items-center gap-1 p-1 w-full bg-slate-50/70 dark:bg-slate-700/30 rounded-full border border-slate-100 dark:border-slate-700">
                  {([
                    { id: 'small' as const,  label: 'Petite' },
                    { id: 'medium' as const, label: 'Moyenne' },
                    { id: 'large' as const,  label: 'Grande' },
                  ]).map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => handleFontSizeChange(id)}
                      className={`flex-1 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                        settings.accessibility.fontSize === id
                          ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                          : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2.5">Langue</p>
                <div className="inline-flex items-center gap-1 p-1 w-full bg-slate-50/70 dark:bg-slate-700/30 rounded-full border border-slate-100 dark:border-slate-700">
                  {([
                    { id: 'fr' as const, label: 'Français' },
                    { id: 'en' as const, label: 'English' },
                  ]).map(({ id, label }) => (
                    <button
                      key={id}
                      onClick={() => setSettings(prev => ({ ...prev, language: id }))}
                      className={`flex-1 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                        settings.language === id
                          ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                          : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-2">La traduction de l'interface n'est pas encore disponible.</p>
              </div>
            </div>
          </div>

          {/* ── Confidentialité ── */}
          <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Confidentialité</h3>
            <p className="text-xs text-slate-400 mb-5">Ce que les autres étudiants peuvent voir de vous.</p>

            <div className="space-y-2">
              {privacyRows.map(({ field, label, hint }) => (
                <div
                  key={field}
                  className="flex items-center justify-between gap-4 px-4 py-3.5 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{hint}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={settings.privacy[field]}
                      onChange={(e) => handleSettingChange('privacy', field, e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-500/30 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900 dark:peer-checked:bg-blue-600" />
                  </label>
                </div>
              ))}
            </div>

            <p className="text-xs text-slate-400 mt-4">
              Ces réglages sont mémorisés mais aucun annuaire étudiant n'est encore exposé sur la plateforme.
            </p>
          </div>
        </div>

        {/* ── Actions avancées ── */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Actions avancées</h3>
          <p className="text-xs text-slate-400 mb-5">Vos données et le support.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={handleExportData}
              className="flex items-center gap-3 px-4 py-4 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-500/40 transition-all text-left"
            >
              <div className="w-10 h-10 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Download className="w-[18px] h-[18px] text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-white">Exporter mes données</p>
                <p className="text-xs text-slate-400 mt-0.5">Profil, cours et devoirs au format JSON</p>
              </div>
            </button>

            <div className="px-4 py-4 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-2xl bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-[18px] h-[18px] text-orange-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 dark:text-white">Signaler un problème</p>
                  <p className="text-xs text-slate-400 mt-0.5">Nous aider à améliorer la plateforme</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href="mailto:gnonlonfoun@ensetmasters.org"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 px-3.5 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <EmailIcon /> Email
                </a>
                <a
                  href="https://wa.me/22996113246"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 px-3.5 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  <WhatsAppIcon /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════
  //  Données dérivées pour le tableau de bord
  // ══════════════════════════════════════════════════════════════
  const dashAssignments = getAccessibleAssignments();
  const dashSubmittedCount = dashAssignments.filter(a => submittedAssignmentIds.has(a.id)).length;
  const dashPendingCount = dashAssignments.length - dashSubmittedCount;
  const dashTotalCourses = accessibleCourses.reduce((total, s) => total + s.cours.length, 0);
  const dashProgress = dashAssignments.length > 0
    ? Math.round((dashSubmittedCount / dashAssignments.length) * 100)
    : 0;

  // Onglets de filtre par semestre (Tous / S1 / S2 / S3)
  const semesterTabs = [
    { id: 'all', label: 'Tous' },
    ...accessibleCourses.map(s => ({ id: s.semestre, label: s.semestre.replace('Semestre ', 'S') }))
  ];

  // Semestres visibles selon le filtre actif
  const dashSemesters = accessibleCourses.filter(
    s => semesterFilter === 'all' || s.semestre === semesterFilter
  );

  // Séries du graphique : cours vs devoirs rendus, par semestre
  const chartData = accessibleCourses.map(s => {
    const semAssignments = dashAssignments.filter(a => a.semester === s.semestre);
    return {
      semestre: s.semestre,
      label: s.semestre.replace('Semestre ', 'S'),
      courses: s.cours.length,
      done: semAssignments.filter(a => submittedAssignmentIds.has(a.id)).length
    };
  });
  const chartMax = Math.max(1, ...chartData.map(d => d.courses));
  const chartTicks = [chartMax, Math.round(chartMax * 0.75), Math.round(chartMax * 0.5), Math.round(chartMax * 0.25), 0];

  // Lignes du tableau « Mes devoirs »
  const dashTableRows = dashAssignments
    .filter(a => semesterFilter === 'all' || a.semester === semesterFilter)
    .filter(a => {
      const q = dashSearch.trim().toLowerCase();
      return q === '' || a.course.toLowerCase().includes(q);
    })
    .slice(0, 6);

  // Bandeau calendrier : 5 jours centrés sur le jour sélectionné
  const calBase = new Date();
  calBase.setDate(calBase.getDate() + calendarOffset * 7);
  const calStrip = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(calBase);
    d.setDate(calBase.getDate() - 2 + i);
    return d;
  });
  const calLabel = calBase.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const formatWeekday = (d: Date) => {
    const w = d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
    return w.charAt(0).toUpperCase() + w.slice(1);
  };

  // Année académique affichée dans la puce de droite
  const academicStartYear = new Date().getMonth() >= 8 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const academicYearLabel = `Année académique ${academicStartYear}-${academicStartYear + 1}`;

  // Rafraîchir la liste des devoirs rendus depuis Supabase
  const refreshSubmitted = async () => {
    if (!studentInfo?.id || isRefreshingDash) return;
    setIsRefreshingDash(true);
    try {
      const ids = await getSubmittedAssignmentIds(studentInfo.id);
      const idSet = applyPromotionReset(ids);
      setSubmittedAssignmentIds(idSet);
      localStorage.setItem(`submitted_assignments_${studentInfo.id}`, JSON.stringify(Array.from(idSet)));
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des devoirs:', error);
    } finally {
      setIsRefreshingDash(false);
    }
  };
  // ── Onglet « Mes cours » ──
  const coursePdfUrl = (semestre: string, fichier: string) =>
    `/cours/${semestre.toLowerCase().replace(/ /g, '')}/${encodeURIComponent(normalizeFileNameForUrl(fichier))}`;

  const courseRawPath = (semestre: string, fichier: string) =>
    `/cours/${semestre.toLowerCase().replace(/ /g, '')}/${fichier}`;

  const semestreDot = (nom: string) =>
    nom === 'Semestre 1' ? 'bg-blue-500' : nom === 'Semestre 2' ? 'bg-green-500' : 'bg-purple-500';

  const coursesQuery = dashSearch.trim().toLowerCase();
  const filteredCourseSemesters = accessibleCourses
    .filter(s => semesterFilter === 'all' || semesterFilter === s.semestre)
    .map(s => ({
      ...s,
      cours: s.cours.filter(c => coursesQuery === '' || c.nom.toLowerCase().includes(coursesQuery)),
    }))
    .filter(s => s.cours.length > 0);
  const filteredCoursesCount = filteredCourseSemesters.reduce((total, s) => total + s.cours.length, 0);

  // ── Onglet « Devoirs » ──
  const assignmentsQuery = dashSearch.trim().toLowerCase();
  const visibleAssignments = getFilteredAssignments().filter(a =>
    assignmentsQuery === '' ||
    a.course.toLowerCase().includes(assignmentsQuery) ||
    (a.semester || '').toLowerCase().includes(assignmentsQuery)
  );

  const assignmentFilterTabs: { id: typeof assignmentFilter; label: string; count: number }[] = [
    { id: 'all',       label: 'Tous',     count: dashAssignments.length },
    { id: 'pending',   label: 'À rendre', count: dashPendingCount },
    { id: 'submitted', label: 'Rendus',   count: dashSubmittedCount },
    { id: 'graded',    label: 'Notés',    count: dashAssignments.filter(a => a.status === 'Noté').length },
  ];

  // Progression devoir par semestre
  const assignmentsBySemester = accessibleCourses
    .map(s => {
      const list = dashAssignments.filter(a => a.semester === s.semestre);
      const done = list.filter(a => submittedAssignmentIds.has(a.id)).length;
      return {
        semestre: s.semestre,
        label: s.semestre.replace('Semestre ', 'S'),
        total: list.length,
        done,
        pct: list.length > 0 ? Math.round((done / list.length) * 100) : 0,
      };
    })
    .filter(s => s.total > 0);

  const deadlineInfo = getGlobalDeadlineStatus();
  const deadlineDaysLeft = globalSubmissionDeadline
    ? Math.ceil((new Date(globalSubmissionDeadline).getTime() - Date.now()) / 86400000)
    : null;

  // ── Onglet « Ma scolarité » ──
  const SCOLARITE_AMOUNT = 451500;
  const LABO_AMOUNT = 200000;
  const totalFees = SCOLARITE_AMOUNT + LABO_AMOUNT;
  const proofsSubmitted = [scolariteProof, laboProof].filter(Boolean).length;
  const dossierPct = Math.round((proofsSubmitted / 2) * 100);
  const anneeAcademique = studentInfo?.annee_academique || `${academicStartYear}–${academicStartYear + 1}`;

  // Envoi d'une preuve de paiement (scolarité ou laboratoire)
  const handleProofUpload = async (
    type: 'scolarite' | 'laboratoire',
    setProof: (p: PaymentProof) => void,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !studentInfo?.email) return;
    setProofUploading(type);
    try {
      const record = await uploadPaymentProof(
        file,
        studentInfo.email,
        studentInfo.nom_complet || studentName,
        studentInfo.matricule,
        type
      );
      setProof(record);
    } catch (err: any) {
      const msg = err?.message || '';
      if (msg.includes('400') || msg.includes('Bucket') || msg.includes('bucket')) {
        alert('Bucket de stockage non configuré. Contactez l’administration.');
      } else {
        alert('Erreur lors de l’envoi : ' + (msg || 'Réessayez.'));
      }
    } finally {
      setProofUploading(null);
      event.target.value = '';
    }
  };

  const feeItems = [
    {
      key: 'scolarite' as const,
      label: 'Frais de scolarité',
      amount: SCOLARITE_AMOUNT,
      icon: GraduationCap,
      iconBg: 'bg-blue-50 dark:bg-blue-500/10',
      iconColor: 'text-blue-600',
      proof: scolariteProof,
      setProof: setScolariteProof,
    },
    {
      key: 'laboratoire' as const,
      label: 'Frais de laboratoire',
      amount: LABO_AMOUNT,
      icon: Award,
      iconBg: 'bg-purple-50 dark:bg-purple-500/10',
      iconColor: 'text-purple-600',
      proof: laboProof,
      setProof: setLaboProof,
    },
  ];


  return (
    <div className="min-h-screen bg-[#F8F7F4] dark:bg-slate-900">

      {/* ── Sidebar desktop ── */}
      <div className={`fixed left-0 top-0 h-full bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-10 hidden md:flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'w-[76px]' : 'w-64'}`}>
        {/* Logo */}
        <div className="px-5 py-5 border-b border-slate-100 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <img src="/logo-enset.png" alt="ENSET" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
            <div className="flex flex-col leading-none">
              <span className="font-bold text-slate-900 dark:text-white text-sm tracking-tight">ENSET-MASTERS</span>
              <span className="text-[10px] text-slate-400">MR-MRTDDEFTP</span>
            </div>
          </div>
        </div>

        {/* Nav */}
        {/* Nav principale */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {!isSidebarCollapsed && (
            <p className="px-3 mb-2 text-[10px] font-semibold tracking-widest text-slate-400 uppercase">Menu</p>
          )}
          <div className="space-y-1">
            {mainMenuItems.map((item) => {
              const isActive = activeTab === item.id;
              const badge = item.id === 'assignments' && dashPendingCount > 0 ? dashPendingCount : null;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  title={isSidebarCollapsed ? item.label : undefined}
                  className={`relative w-full flex items-center rounded-xl transition-all duration-200 text-sm font-medium ${
                    isSidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
                  } ${
                    isActive
                      ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                  }`}
                >
                  <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
                  {!isSidebarCollapsed && <span className="flex-1 text-left">{item.label}</span>}
                  {badge !== null && (
                    isSidebarCollapsed ? (
                      <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-orange-500 border-2 border-white dark:border-slate-900" />
                    ) : (
                      <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                        isActive ? 'bg-white/20 text-white' : 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400'
                      }`}>
                        {badge}
                      </span>
                    )
                  )}
                </button>
              );
            })}
          </div>

          {/* Carte de rappel */}
          {!isSidebarCollapsed && (
            <div className="mt-6 mx-1 rounded-2xl bg-slate-900 dark:bg-slate-800 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-3.5 h-3.5 text-blue-400" />
                </div>
                <p className="text-white text-sm font-semibold">
                  {globalSubmissionDeadline ? 'Date limite' : 'Vos devoirs'}
                </p>
              </div>
              <p className="text-[11px] leading-snug text-slate-400 mb-3">
                {globalSubmissionDeadline
                  ? `Dépôt des devoirs jusqu'au ${new Date(globalSubmissionDeadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}.`
                  : `${dashPendingCount} devoir${dashPendingCount > 1 ? 's' : ''} en attente de dépôt.`}
              </p>
              <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden mb-3">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-700"
                  style={{ width: `${dashProgress}%` }}
                />
              </div>
              <button
                onClick={() => setActiveTab('assignments')}
                className="w-full py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors"
              >
                Voir les devoirs
              </button>
            </div>
          )}
        </nav>

        {/* Nav secondaire + déconnexion */}
        <div className="px-3 py-4 border-t border-slate-100 dark:border-slate-800 space-y-1">
          {bottomMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              title={isSidebarCollapsed ? item.label : undefined}
              className={`w-full flex items-center rounded-xl transition-all duration-200 text-sm font-medium ${
                isSidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'
              } ${
                activeTab === item.id
                  ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              {!isSidebarCollapsed && <span>{item.label}</span>}
            </button>
          ))}

          {/* Toggle thème */}
          <button
            onClick={() => handleThemeChange(settings.theme === 'dark' ? 'light' : 'dark')}
            title={settings.theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
            className={`w-full flex items-center rounded-xl transition-all duration-200 text-sm font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white ${isSidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'}`}
          >
            {settings.theme === 'dark' ? (
              <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            ) : (
              <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            {!isSidebarCollapsed && <span>{settings.theme === 'dark' ? 'Mode clair' : 'Mode sombre'}</span>}
          </button>

          {/* Déconnexion */}
          <button
            onClick={onLogout}
            title={isSidebarCollapsed ? 'Déconnexion' : undefined}
            className={`w-full flex items-center rounded-xl transition-all duration-200 text-sm font-medium text-slate-500 hover:text-red-600 hover:bg-red-50 dark:text-slate-400 dark:hover:bg-red-500/10 ${isSidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2.5'}`}
          >
            <LogOut className="w-[18px] h-[18px] flex-shrink-0" />
            {!isSidebarCollapsed && <span>Déconnexion</span>}
          </button>

          {/* Réduire */}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            title={isSidebarCollapsed ? 'Agrandir' : 'Réduire'}
            className={`w-full flex items-center rounded-xl transition-all duration-200 text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 dark:hover:text-slate-200 ${isSidebarCollapsed ? 'justify-center px-2 py-2.5' : 'gap-3 px-3 py-2'}`}
          >
            <svg className={`w-[18px] h-[18px] flex-shrink-0 transition-transform duration-300 ${isSidebarCollapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
            {!isSidebarCollapsed && <span>Réduire</span>}
          </button>
        </div>
      </div>

      {/* ── Mobile header ── */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-20 bg-[#F8F7F4]/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/60 dark:border-slate-800">
        <div className="h-14 px-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo-enset.png" alt="ENSET" className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
            <div className="flex flex-col leading-none">
              <span className="font-bold text-slate-900 dark:text-white text-sm tracking-tight">ENSET-MASTERS</span>
              <span className="text-[10px] text-slate-400">MR-MRTDDEFTP</span>
            </div>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(true)}
            className="w-9 h-9 rounded-xl bg-white border border-slate-200 shadow-sm flex flex-col items-center justify-center gap-[4px]"
            aria-label="Menu"
          >
            <span className="w-4 h-[1.5px] bg-slate-600 rounded-full" />
            <span className="w-4 h-[1.5px] bg-slate-600 rounded-full" />
            <span className="w-4 h-[1.5px] bg-slate-600 rounded-full" />
          </button>
        </div>
      </div>

      {/* ── Drawer mobile ── */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/10 backdrop-blur-[2px]" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="relative w-64 bg-white dark:bg-slate-900 h-full shadow-xl flex flex-col border-r border-slate-200 dark:border-slate-800">
            {/* Header drawer */}
            <div className="px-5 py-5 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <img src="/logo-enset.png" alt="ENSET" className="w-7 h-7 rounded-md object-contain flex-shrink-0" />
                <div className="flex flex-col leading-none">
                  <span className="font-bold text-slate-800 dark:text-white text-sm">ENSET-MASTERS</span>
                  <span className="text-[10px] text-slate-400">MR-MRTDDEFTP</span>
                </div>
              </div>
              <button onClick={() => setIsMobileMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {/* Nav */}
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {menuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); setIsMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-sm font-medium ${
                    activeTab === item.id
                      ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                      : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
                  }`}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
            {/* User + logout */}
            <div className="px-4 py-4 border-t border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2.5 mb-3 px-1">
                <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-slate-500" />
                </div>
                <div className="flex flex-col leading-none min-w-0">
                  <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{studentInfo?.nom_complet || studentName}</span>
                  <span className="text-[10px] text-slate-400">Étudiant Master</span>
                </div>
              </div>
              <button
                onClick={() => { setIsMobileMenuOpen(false); onLogout(); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
              >
                <LogOut className="w-4 h-4" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Main Content ── */}
      <div className={`md:pt-0 pt-14 transition-all duration-300 ${isSidebarCollapsed ? 'md:ml-[76px]' : 'md:ml-64'}`}>
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1440px] mx-auto">
        {selectedCourse ? (
          <CourseDetail course={selectedCourse} onBack={handleBackToDashboard} />
        ) : (
          <>
            {/* ── Topbar ── */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="min-w-0">
                <h1 className="text-2xl sm:text-[28px] leading-tight font-bold text-slate-900 dark:text-white tracking-tight">
                  {activeTab === 'dashboard'   ? 'Tableau de bord' :
                   activeTab === 'courses'     ? 'Mes cours' :
                   activeTab === 'assignments' ? 'Devoirs' :
                   activeTab === 'scolarite'   ? 'Ma scolarité' :
                   activeTab === 'profile'     ? 'Profil' : 'Paramètres'}
                </h1>
                <p className="hidden sm:block text-sm text-slate-400 mt-0.5 truncate">
                  Bonjour {(studentInfo?.nom_complet || studentName).split(' ')[0]} — Master MR-MRTDDEFTP
                </p>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {/* Recherche */}
                <div className="relative hidden md:block">
                  <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={dashSearch}
                    onChange={(e) => setDashSearch(e.target.value)}
                    placeholder="Rechercher un cours..."
                    className="w-52 lg:w-72 h-11 pl-11 pr-4 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-300 transition-all"
                  />
                </div>

                {/* Notifications */}
                <button
                  onClick={() => setShowNotifications(true)}
                  aria-label="Notifications"
                  className="w-11 h-11 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full flex items-center justify-center text-slate-500 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:shadow-md transition-all relative"
                >
                  <Bell className="w-[18px] h-[18px]" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center font-bold border-2 border-white dark:border-slate-800">
                      {unreadCount}
                    </span>
                  )}
                </button>

                {/* Avatar */}
                <button
                  onClick={() => setActiveTab('profile')}
                  aria-label="Profil"
                  className="w-11 h-11 rounded-full overflow-hidden border border-slate-200 dark:border-slate-700 bg-blue-600 flex items-center justify-center text-white text-sm font-semibold hover:shadow-md transition-all flex-shrink-0"
                >
                  {profilePhoto ? (
                    <img src={profilePhoto} alt="Profil" className="w-full h-full object-cover" />
                  ) : (
                    (studentInfo?.nom_complet || studentName).charAt(0).toUpperCase()
                  )}
                </button>
              </div>
            </div>

            {/* Dashboard Content */}
            {activeTab === 'dashboard' && (
              <div className="space-y-4 sm:space-y-5">

                {/* ── Filtres semestre + année académique ── */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                    {semesterTabs.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setSemesterFilter(tab.id)}
                        className={`px-4 sm:px-5 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                          semesterFilter === tab.id
                            ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                            : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                    <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">{academicYearLabel}</span>
                  </div>
                </div>

                {/* ── Indicateurs ── */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {/* Carte mise en avant */}
                  <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-5 sm:p-6 shadow-sm">
                    <p className="text-sm text-slate-400 mb-3">Cours disponibles</p>
                    <p className="text-[32px] leading-none font-bold text-white tracking-tight mb-3">{dashTotalCourses}</p>
                    <div className="flex items-center gap-1.5">
                      <BookOpen className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                      <span className="text-xs text-slate-400">
                        {studentProfile.studyYear === 1 ? 'Semestres 1 & 2' : 'Semestre 3 + 1 cours de S1'}
                      </span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <p className="text-sm text-slate-400 mb-3">Devoirs à rendre</p>
                    <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{dashPendingCount}</p>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                      <span className="text-xs text-slate-400">sur {dashAssignments.length} devoirs</span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <p className="text-sm text-slate-400 mb-3">Devoirs rendus</p>
                    <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{dashSubmittedCount}</p>
                    <div className="flex items-center gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                      <span className="text-xs font-semibold text-green-600">{dashProgress}%</span>
                      <span className="text-xs text-slate-400">complété</span>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <p className="text-sm text-slate-400 mb-3">Année d'étude</p>
                    <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">
                      {studentProfile.studyYear === 1 ? '1ère' : '2ème'}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <GraduationCap className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span className="text-xs text-slate-400">Master MR-MRTDDEFTP</span>
                    </div>
                  </div>
                </div>

                {/* ── Graphique + calendrier ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                  {/* Graphique par semestre */}
                  <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Aperçu par semestre</h2>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="flex items-center gap-1.5 text-xs text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-full bg-slate-900 dark:bg-slate-500" /> Cours
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-slate-400">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> Devoirs rendus
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => setActiveTab('courses')}
                        aria-label="Voir tous les cours"
                        className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="flex gap-4">
                      {/* Axe des ordonnées */}
                      <div className="flex flex-col justify-between h-52 text-[11px] text-slate-400 tabular-nums pb-6 flex-shrink-0">
                        {chartTicks.map((t, i) => (
                          <span key={i}>{t}</span>
                        ))}
                      </div>

                      {/* Barres */}
                      <div className="flex-1 flex items-end justify-around gap-3 sm:gap-8 h-52 border-l border-slate-100 dark:border-slate-700 pl-4 sm:pl-6">
                        {chartData.map((d) => {
                          const isActive = semesterFilter === 'all' || semesterFilter === d.semestre;
                          return (
                            <div key={d.semestre} className="flex-1 flex flex-col items-center gap-2 h-full justify-end">
                              <div className="flex items-end justify-center gap-1.5 sm:gap-2 w-full h-full">
                                <div
                                  title={`${d.courses} cours`}
                                  style={{ height: `${Math.max(6, (d.courses / chartMax) * 100)}%` }}
                                  className={`w-6 sm:w-9 rounded-full transition-all duration-500 ${
                                    isActive ? 'bg-slate-900 dark:bg-slate-500' : 'bg-slate-200 dark:bg-slate-700'
                                  }`}
                                />
                                <div
                                  title={`${d.done} devoir(s) rendu(s)`}
                                  style={{ height: `${Math.max(6, (d.done / chartMax) * 100)}%` }}
                                  className={`w-6 sm:w-9 rounded-full transition-all duration-500 ${
                                    isActive ? 'bg-blue-500' : 'bg-blue-100 dark:bg-blue-900/40'
                                  }`}
                                />
                              </div>
                              <span className={`text-xs pb-1 ${isActive ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                                {d.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Calendrier + progression */}
                  <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                    {/* Navigation du mois */}
                    <div className="flex items-center justify-between mb-5">
                      <button
                        onClick={() => setCalendarOffset(calendarOffset - 1)}
                        aria-label="Semaine précédente"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white transition-colors"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <span className="text-sm font-semibold text-slate-900 dark:text-white capitalize">{calLabel}</span>
                      <button
                        onClick={() => setCalendarOffset(calendarOffset + 1)}
                        aria-label="Semaine suivante"
                        className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-white transition-colors"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Bandeau de jours */}
                    <div className="grid grid-cols-5 gap-1">
                      {calStrip.map((d, i) => {
                        const isSelected = i === 2;
                        return (
                          <div
                            key={d.toISOString()}
                            className={`flex flex-col items-center gap-3 py-3 rounded-full transition-all duration-200 ${
                              isSelected ? 'bg-slate-100 dark:bg-slate-700 shadow-inner' : ''
                            }`}
                          >
                            <span className={`text-xs ${isSelected ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-400'}`}>
                              {formatWeekday(d)}
                            </span>
                            <span className={`text-sm tabular-nums ${isSelected ? 'font-bold text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}>
                              {d.getDate()}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="border-t border-slate-100 dark:border-slate-700 my-5" />

                    {/* Jauge de progression */}
                    <div className="flex items-center justify-between gap-4 mt-auto">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 dark:text-white mb-1.5">Progression</p>
                        <div className="flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">{dashSubmittedCount} sur {dashAssignments.length} rendus</span>
                        </div>
                      </div>
                      <div className="relative w-16 h-16 flex-shrink-0">
                        <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                          <circle cx="32" cy="32" r="27" fill="none" strokeWidth="7" className="stroke-slate-100 dark:stroke-slate-700" />
                          <circle
                            cx="32" cy="32" r="27" fill="none" strokeWidth="7" strokeLinecap="round"
                            className="stroke-blue-600 transition-all duration-700"
                            strokeDasharray={`${(dashProgress / 100) * 169.6} 169.6`}
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white tabular-nums">
                          {dashProgress}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Tableau des devoirs ── */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-5 sm:px-6 py-5">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Mes devoirs</h2>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={refreshSubmitted}
                        aria-label="Rafraîchir"
                        className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <RotateCcw className={`w-4 h-4 ${isRefreshingDash ? 'animate-spin' : ''}`} />
                      </button>
                      <button
                        onClick={() => setActiveTab('assignments')}
                        aria-label="Voir tous les devoirs"
                        className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                      >
                        <ArrowUpRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[640px]">
                      <thead>
                        <tr className="border-y border-slate-100 dark:border-slate-700">
                          <th className="text-left text-xs font-medium text-slate-400 px-5 sm:px-6 py-3">Cours</th>
                          <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Semestre</th>
                          <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Type</th>
                          <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Points</th>
                          <th className="text-left text-xs font-medium text-slate-400 px-5 sm:px-6 py-3">Statut</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashTableRows.map((a) => {
                          const isDone = submittedAssignmentIds.has(a.id);
                          return (
                            <tr
                              key={a.id}
                              onClick={() => setActiveTab('assignments')}
                              className="border-b border-slate-50 dark:border-slate-700/50 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors cursor-pointer"
                            >
                              <td className="px-5 sm:px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                  </div>
                                  <span className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1 max-w-[280px]">
                                    {a.course}
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{a.semester}</td>
                              <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400">{a.type}</td>
                              <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 tabular-nums">{a.points} pts</td>
                              <td className="px-5 sm:px-6 py-4">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                  isDone
                                    ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                    : 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400'
                                }`}>
                                  {isDone ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                  {isDone ? 'Rendu' : 'À rendre'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                        {dashTableRows.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-6 py-12 text-center">
                              <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-3">
                                <ClipboardCheck className="w-5 h-5 text-slate-400" />
                              </div>
                              <p className="text-sm text-slate-500 dark:text-slate-400">Aucun devoir ne correspond à votre recherche.</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── Cours récents ── */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight">Mes cours</h2>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {studentProfile.studyYear === 1
                          ? "Accès aux Semestres 1 & 2 — le S3 s'ouvrira en 2ème année."
                          : "Accès au Semestre 3, plus le cours de Manuels Pédagogiques du S1."}
                      </p>
                    </div>
                    <button
                      onClick={() => setActiveTab('courses')}
                      aria-label="Voir tous les cours"
                      className="w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
                    >
                      <ArrowUpRight className="w-4 h-4" />
                    </button>
                  </div>

                  {dashSemesters.length > 0 ? (
                    <div className="space-y-6">
                      {dashSemesters.map((semestre) => {
                        const query = dashSearch.trim().toLowerCase();
                        const filtered = semestre.cours.filter(c => query === '' || c.nom.toLowerCase().includes(query));
                        if (filtered.length === 0) return null;
                        return (
                          <div key={semestre.semestre}>
                            <div className="flex items-center gap-2 mb-3">
                              <span className={`w-2 h-2 rounded-full ${
                                semestre.semestre === 'Semestre 1' ? 'bg-blue-500' :
                                semestre.semestre === 'Semestre 2' ? 'bg-green-500' : 'bg-purple-500'
                              }`} />
                              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{semestre.semestre}</h3>
                              <span className="text-[11px] px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded-full text-slate-500 dark:text-slate-400">
                                {filtered.length} cours
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                              {filtered.slice(0, 6).map((cours) => {
                                const pdfUrl = `/cours/${semestre.semestre.toLowerCase().replace(/ /g, '')}/${encodeURIComponent(normalizeFileNameForUrl(cours.fichier))}`;
                                return (
                                  <div
                                    key={cours.fichier}
                                    className="rounded-2xl p-4 bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-500/40 hover:shadow-sm transition-all duration-200 flex flex-col"
                                  >
                                    <div className="flex items-start gap-3 mb-4 flex-1">
                                      <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-4 h-4 text-blue-600" />
                                      </div>
                                      <p className="font-medium text-slate-900 dark:text-white text-sm leading-snug line-clamp-2">{cours.nom}</p>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setPdfViewer({ url: pdfUrl, name: cours.nom })}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 dark:bg-blue-600 text-white text-xs rounded-xl hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors font-medium"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        Lire
                                      </button>
                                      <a
                                        href={pdfUrl}
                                        download={cours.fichier}
                                        className="flex items-center justify-center px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium"
                                        aria-label="Télécharger"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </a>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {filtered.length > 6 && (
                              <button
                                onClick={() => setActiveTab('courses')}
                                className="mt-3 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                              >
                                Voir les {filtered.length} cours du {semestre.semestre}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10">
                      <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <BookOpen className="w-6 h-6 text-slate-400" />
                      </div>
                      <h3 className="text-base font-medium text-slate-900 dark:text-white mb-1">Aucun cours disponible</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        Contactez l'administration si vous pensez que cela est une erreur.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Other tabs content */}
            {activeTab !== 'dashboard' && (
              <div className="space-y-6">
                {activeTab === 'courses' && (
                  <div className="space-y-4 sm:space-y-5">

                    {/* ── Filtres semestre + vue ── */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                        {semesterTabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => setSemesterFilter(tab.id)}
                            className={`px-4 sm:px-5 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                              semesterFilter === tab.id
                                ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                            }`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                          <GraduationCap className="w-4 h-4 text-slate-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                            {studentProfile.studyYear === 1 ? '1ère année' : '2ème année'}
                          </span>
                        </div>

                        {/* Basculer grille / liste */}
                        <div className="inline-flex items-center gap-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                          <button
                            onClick={() => setCoursesView('grid')}
                            aria-label="Vue grille"
                            title="Vue grille"
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                              coursesView === 'grid'
                                ? 'bg-slate-900 text-white dark:bg-blue-600'
                                : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
                            }`}
                          >
                            <LayoutGrid className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setCoursesView('list')}
                            aria-label="Vue liste"
                            title="Vue liste"
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                              coursesView === 'list'
                                ? 'bg-slate-900 text-white dark:bg-blue-600'
                                : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
                            }`}
                          >
                            <List className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Indicateurs ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-5 sm:p-6 shadow-sm">
                        <p className="text-sm text-slate-400 mb-3">Cours disponibles</p>
                        <p className="text-[32px] leading-none font-bold text-white tracking-tight mb-3">{dashTotalCourses}</p>
                        <div className="flex items-center gap-1.5">
                          <BookOpen className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-slate-400">
                            {studentProfile.studyYear === 1 ? 'Semestres 1 & 2' : 'Semestre 3 + 1 cours de S1'}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Semestres ouverts</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{accessibleCourses.length}</p>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">
                            {accessibleCourses.map(s => s.semestre.replace('Semestre ', 'S')).join(' · ')}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Cours affichés</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{filteredCoursesCount}</p>
                        <div className="flex items-center gap-1.5">
                          <Search className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">
                            {coursesQuery || semesterFilter !== 'all' ? 'Filtre actif' : 'Aucun filtre'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ── Avertissement 1ère année ── */}
                    {studentProfile.studyYear === 1 && (
                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 border border-amber-200 dark:border-amber-500/30 shadow-sm">
                        <div className="flex items-start gap-3">
                          <div className="w-9 h-9 rounded-xl bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-0.5">Semestre 3 verrouillé</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                              Vous êtes en 1ère année. Les cours du Semestre 3 s'ouvriront après votre promotion en 2ème année par l'administration.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── Liste des cours ── */}
                    {filteredCourseSemesters.length > 0 ? (
                      <div className="space-y-4 sm:space-y-5">
                        {filteredCourseSemesters.map((semestre) => (
                          <div
                            key={semestre.semestre}
                            className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden"
                          >
                            {/* En-tête du semestre */}
                            <div className="flex items-center justify-between px-5 sm:px-6 py-5">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${semestreDot(semestre.semestre)}`} />
                                <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight truncate">
                                  {semestre.semestre}
                                </h2>
                                <span className="text-[11px] px-2.5 py-1 bg-slate-100 dark:bg-slate-700 rounded-full text-slate-500 dark:text-slate-400 flex-shrink-0">
                                  {semestre.cours.length} cours
                                </span>
                              </div>
                            </div>

                            {coursesView === 'grid' ? (
                              /* Vue grille */
                              <div className="px-5 sm:px-6 pb-6 pt-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                {semestre.cours.map((cours) => (
                                  <div
                                    key={cours.fichier}
                                    className="rounded-2xl p-4 bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-500/40 hover:shadow-sm transition-all duration-200 flex flex-col"
                                  >
                                    <div className="flex items-start gap-3 mb-4 flex-1">
                                      <div className="w-9 h-9 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-4 h-4 text-blue-600" />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-slate-900 dark:text-white text-sm leading-snug line-clamp-2">{cours.nom}</p>
                                        {cours.professeur && (
                                          <p className="text-xs text-slate-400 mt-1 truncate">{cours.professeur}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setPdfViewer({ url: coursePdfUrl(semestre.semestre, cours.fichier), name: cours.nom })}
                                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-900 dark:bg-blue-600 text-white text-xs rounded-xl hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors font-medium"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                        Lire
                                      </button>
                                      <button
                                        onClick={() => handleCourseDownload(cours.nom, courseRawPath(semestre.semestre, cours.fichier))}
                                        aria-label={`Télécharger ${cours.nom}`}
                                        title="Télécharger"
                                        className="flex items-center justify-center px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-xs rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors font-medium"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              /* Vue liste */
                              <div className="overflow-x-auto">
                                <table className="w-full min-w-[640px]">
                                  <thead>
                                    <tr className="border-y border-slate-100 dark:border-slate-700">
                                      <th className="text-left text-xs font-medium text-slate-400 px-5 sm:px-6 py-3 w-12">#</th>
                                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Intitulé</th>
                                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Enseignant</th>
                                      <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Format</th>
                                      <th className="text-right text-xs font-medium text-slate-400 px-5 sm:px-6 py-3">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {semestre.cours.map((cours, index) => (
                                      <tr
                                        key={cours.fichier}
                                        className="border-b border-slate-50 dark:border-slate-700/50 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors"
                                      >
                                        <td className="px-5 sm:px-6 py-4 text-sm text-slate-400 tabular-nums">
                                          {String(index + 1).padStart(2, '0')}
                                        </td>
                                        <td className="px-4 py-4">
                                          <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                                              <FileText className="w-4 h-4 text-blue-600" />
                                            </div>
                                            <span className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1 max-w-[320px]">
                                              {cours.nom}
                                            </span>
                                          </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                          {cours.professeur || '—'}
                                        </td>
                                        <td className="px-4 py-4">
                                          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                                            PDF
                                          </span>
                                        </td>
                                        <td className="px-5 sm:px-6 py-4">
                                          <div className="flex items-center justify-end gap-2">
                                            <button
                                              onClick={() => setPdfViewer({ url: coursePdfUrl(semestre.semestre, cours.fichier), name: cours.nom })}
                                              className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-slate-900 dark:bg-blue-600 text-white text-xs rounded-xl hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors font-medium"
                                            >
                                              <Eye className="w-3.5 h-3.5" />
                                              Lire
                                            </button>
                                            <button
                                              onClick={() => handleCourseDownload(cours.nom, courseRawPath(semestre.semestre, cours.fichier))}
                                              aria-label={`Télécharger ${cours.nom}`}
                                              title="Télécharger"
                                              className="inline-flex items-center justify-center w-9 h-9 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                            >
                                              <Download className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-14 px-6">
                        <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                          <BookOpen className="w-6 h-6 text-slate-400" />
                        </div>
                        <h3 className="text-base font-medium text-slate-900 dark:text-white mb-1">
                          {coursesQuery || semesterFilter !== 'all' ? 'Aucun résultat' : 'Aucun cours disponible'}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                          {coursesQuery || semesterFilter !== 'all'
                            ? 'Aucun cours ne correspond à votre recherche ou au semestre choisi.'
                            : "Contactez l'administration si vous pensez que cela est une erreur."}
                        </p>
                        {(coursesQuery || semesterFilter !== 'all') && (
                          <button
                            onClick={() => { setDashSearch(''); setSemesterFilter('all'); }}
                            className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-slate-900 dark:bg-blue-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors"
                          >
                            Réinitialiser les filtres
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'assignments' && (
                  <div className="space-y-4 sm:space-y-5">

                    {/* ── Filtres statut + deadline + vue ── */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                        {assignmentFilterTabs.map((tab) => (
                          <button
                            key={tab.id}
                            onClick={() => setAssignmentFilter(tab.id)}
                            className={`inline-flex items-center gap-2 pl-4 pr-2.5 h-9 rounded-full text-sm font-medium transition-all duration-200 ${
                              assignmentFilter === tab.id
                                ? 'bg-slate-900 text-white shadow-sm dark:bg-blue-600'
                                : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                            }`}
                          >
                            {tab.label}
                            <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                              assignmentFilter === tab.id
                                ? 'bg-white/20 text-white'
                                : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                            }`}>
                              {tab.count}
                            </span>
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 sm:gap-3">
                        {globalSubmissionDeadline && (
                          <div className={`inline-flex items-center gap-2 h-11 px-4 rounded-full border shadow-sm ${
                            deadlineInfo.isPast
                              ? 'bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30'
                              : deadlineInfo.isApproaching
                                ? 'bg-amber-50 border-amber-200 dark:bg-amber-500/10 dark:border-amber-500/30'
                                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                          }`}>
                            <Calendar className={`w-4 h-4 flex-shrink-0 ${
                              deadlineInfo.isPast ? 'text-red-500' : deadlineInfo.isApproaching ? 'text-amber-600' : 'text-slate-400'
                            }`} />
                            <span className={`text-sm font-medium whitespace-nowrap ${
                              deadlineInfo.isPast
                                ? 'text-red-700 dark:text-red-400'
                                : deadlineInfo.isApproaching
                                  ? 'text-amber-700 dark:text-amber-400'
                                  : 'text-slate-600 dark:text-slate-300'
                            }`}>
                              {deadlineInfo.isPast
                                ? 'Délai dépassé'
                                : deadlineInfo.isApproaching
                                  ? `Plus que ${deadlineInfo.hoursLeft} h`
                                  : `${deadlineDaysLeft} j restants`}
                            </span>
                          </div>
                        )}

                        {/* Basculer grille / liste */}
                        <div className="inline-flex items-center gap-1 p-1 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                          <button
                            onClick={() => setAssignmentsView('grid')}
                            aria-label="Vue grille"
                            title="Vue grille"
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                              assignmentsView === 'grid'
                                ? 'bg-slate-900 text-white dark:bg-blue-600'
                                : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
                            }`}
                          >
                            <LayoutGrid className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setAssignmentsView('list')}
                            aria-label="Vue liste"
                            title="Vue liste"
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                              assignmentsView === 'list'
                                ? 'bg-slate-900 text-white dark:bg-blue-600'
                                : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
                            }`}
                          >
                            <List className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* ── Indicateurs ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-5 sm:p-6 shadow-sm">
                        <p className="text-sm text-slate-400 mb-3">Total devoirs</p>
                        <p className="text-[32px] leading-none font-bold text-white tracking-tight mb-3">{dashAssignments.length}</p>
                        <div className="flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-slate-400">
                            {studentProfile.studyYear === 1 ? 'Semestres 1 & 2' : 'Semestre 3 + 1 cours de S1'}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">À rendre</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{dashPendingCount}</p>
                        <div className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                          <span className="text-xs text-slate-400">
                            {deadlineInfo.isPast ? 'Délai dépassé' : 'En attente de dépôt'}
                          </span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Rendus</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{dashSubmittedCount}</p>
                        <div className="flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">sur {dashAssignments.length} devoirs</span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Progression</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3">{dashProgress}%</p>
                        <div className="flex items-center gap-1.5">
                          <TrendingUp className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">complété</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Progression par semestre + jauge ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
                        <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Progression par semestre</h2>
                        <p className="text-xs text-slate-400 mb-5">Devoirs déposés sur le total accessible</p>

                        <div className="space-y-5">
                          {assignmentsBySemester.map((s) => (
                            <div key={s.semestre}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                                  <span className={`w-2 h-2 rounded-full ${semestreDot(s.semestre)}`} />
                                  {s.semestre}
                                </span>
                                <span className="text-sm text-slate-400 tabular-nums">{s.done} / {s.total}</span>
                              </div>
                              <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5 overflow-hidden">
                                <div
                                  className="bg-slate-900 dark:bg-blue-500 h-2.5 rounded-full transition-all duration-700"
                                  style={{ width: `${s.pct}%` }}
                                />
                              </div>
                            </div>
                          ))}
                          {assignmentsBySemester.length === 0 && (
                            <p className="text-sm text-slate-400 py-6 text-center">Aucun devoir accessible pour le moment.</p>
                          )}
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Date limite</h2>
                          <p className="text-xs text-slate-400">
                            {globalSubmissionDeadline
                              ? new Date(globalSubmissionDeadline).toLocaleDateString('fr-FR', {
                                  day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                                })
                              : 'Aucune date limite définie.'}
                          </p>
                        </div>

                        <div className="flex items-center justify-between gap-4 mt-6">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white mb-1.5">Avancement</p>
                            <div className="flex items-center gap-1.5">
                              <CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                              <span className="text-xs text-slate-400">{dashSubmittedCount} sur {dashAssignments.length} rendus</span>
                            </div>
                          </div>
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                              <circle cx="32" cy="32" r="27" fill="none" strokeWidth="7" className="stroke-slate-100 dark:stroke-slate-700" />
                              <circle
                                cx="32" cy="32" r="27" fill="none" strokeWidth="7" strokeLinecap="round"
                                className="stroke-blue-600 transition-all duration-700"
                                strokeDasharray={`${(dashProgress / 100) * 169.6} 169.6`}
                              />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white tabular-nums">
                              {dashProgress}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Liste des devoirs ── */}
                    {visibleAssignments.length > 0 ? (
                      assignmentsView === 'grid' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                          {visibleAssignments.map(assignment => (
                            <AssignmentCard key={assignment.id} assignment={assignment} />
                          ))}
                        </div>
                      ) : (
                        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[720px]">
                              <thead>
                                <tr className="border-b border-slate-100 dark:border-slate-700">
                                  <th className="text-left text-xs font-medium text-slate-400 px-5 sm:px-6 py-3 w-12">#</th>
                                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Cours</th>
                                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Semestre</th>
                                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Points</th>
                                  <th className="text-left text-xs font-medium text-slate-400 px-4 py-3">Statut</th>
                                  <th className="text-right text-xs font-medium text-slate-400 px-5 sm:px-6 py-3">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {visibleAssignments.map((a, index) => {
                                  const isDone = submittedAssignmentIds.has(a.id);
                                  return (
                                    <tr
                                      key={a.id}
                                      className="border-b border-slate-50 dark:border-slate-700/50 last:border-0 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors"
                                    >
                                      <td className="px-5 sm:px-6 py-4 text-sm text-slate-400 tabular-nums">
                                        {String(index + 1).padStart(2, '0')}
                                      </td>
                                      <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                            isDone ? 'bg-green-50 dark:bg-green-500/10' : 'bg-blue-50 dark:bg-blue-500/10'
                                          }`}>
                                            <FileText className={`w-4 h-4 ${isDone ? 'text-green-600' : 'text-blue-600'}`} />
                                          </div>
                                          <span className="text-sm font-medium text-slate-900 dark:text-white line-clamp-1 max-w-[300px]">
                                            {a.course}
                                          </span>
                                        </div>
                                      </td>
                                      <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 whitespace-nowrap">{a.semester}</td>
                                      <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 tabular-nums">{a.points} pts</td>
                                      <td className="px-4 py-4">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                          isDone
                                            ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                            : 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400'
                                        }`}>
                                          {isDone ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                          {isDone ? 'Rendu' : 'À rendre'}
                                        </span>
                                      </td>
                                      <td className="px-5 sm:px-6 py-4 text-right">
                                        <button
                                          onClick={() => { if (!isDone && !deadlineInfo.isPast) handleSubmitAssignment(a); }}
                                          disabled={isDone || deadlineInfo.isPast}
                                          className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium transition-colors ${
                                            isDone || deadlineInfo.isPast
                                              ? 'bg-slate-100 text-slate-400 cursor-default dark:bg-slate-700 dark:text-slate-500'
                                              : 'bg-slate-900 dark:bg-blue-600 text-white hover:bg-slate-800 dark:hover:bg-blue-700'
                                          }`}
                                        >
                                          {isDone ? 'Remis' : deadlineInfo.isPast ? 'Délai dépassé' : 'Rendre'}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm text-center py-14 px-6">
                        <div className="w-14 h-14 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                          <ClipboardCheck className="w-6 h-6 text-slate-400" />
                        </div>
                        <h3 className="text-base font-medium text-slate-900 dark:text-white mb-1">Aucun devoir trouvé</h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                          Aucun devoir ne correspond au filtre « {assignmentFilterTabs.find(t => t.id === assignmentFilter)?.label} »
                          {assignmentsQuery && ' ni à votre recherche'}.
                        </p>
                        {(assignmentFilter !== 'all' || assignmentsQuery) && (
                          <button
                            onClick={() => { setAssignmentFilter('all'); setDashSearch(''); }}
                            className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-slate-900 dark:bg-blue-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors"
                          >
                            Réinitialiser les filtres
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {activeTab === 'scolarite' && (
                  <div className="space-y-4 sm:space-y-5">

                    {/* ── Statut + année académique ── */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                        <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">Inscrit(e) — Actif</span>
                      </div>

                      <div className="inline-flex items-center gap-2 h-11 px-4 bg-white dark:bg-slate-800 rounded-full border border-slate-200 dark:border-slate-700 shadow-sm">
                        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300 whitespace-nowrap">
                          Année académique {anneeAcademique}
                        </span>
                      </div>
                    </div>

                    {/* ── Indicateurs ── */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                      <div className="bg-slate-900 dark:bg-slate-800 rounded-3xl p-5 sm:p-6 shadow-sm">
                        <p className="text-sm text-slate-400 mb-3">Total des frais</p>
                        <p className="text-[32px] leading-none font-bold text-white tracking-tight mb-3 tabular-nums">
                          {totalFees.toLocaleString('fr-FR')}
                        </p>
                        <div className="flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-slate-400">FCFA · scolarité + laboratoire</span>
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Frais de scolarité</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3 tabular-nums">
                          {SCOLARITE_AMOUNT.toLocaleString('fr-FR')}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {scolariteProof ? (
                            <><CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-xs text-slate-400">Preuve envoyée</span></>
                          ) : (
                            <><Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /><span className="text-xs text-slate-400">En attente de preuve</span></>
                          )}
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Frais de laboratoire</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3 tabular-nums">
                          {LABO_AMOUNT.toLocaleString('fr-FR')}
                        </p>
                        <div className="flex items-center gap-1.5">
                          {laboProof ? (
                            <><CheckCircle className="w-3.5 h-3.5 text-green-600 flex-shrink-0" /><span className="text-xs text-slate-400">Preuve envoyée</span></>
                          ) : (
                            <><Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /><span className="text-xs text-slate-400">En attente de preuve</span></>
                          )}
                        </div>
                      </div>

                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition-shadow duration-200">
                        <p className="text-sm text-slate-400 mb-3">Preuves envoyées</p>
                        <p className="text-[32px] leading-none font-bold text-slate-900 dark:text-white tracking-tight mb-3 tabular-nums">
                          {proofsSubmitted} / 2
                        </p>
                        <div className="flex items-center gap-1.5">
                          <BadgeCheck className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                          <span className="text-xs text-slate-400">Dossier {dossierPct}% complet</span>
                        </div>
                      </div>
                    </div>

                    {/* ── Frais + dossier ── */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                      {/* Les deux cartes de frais */}
                      <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {feeItems.map(({ key, label, amount, icon: Icon, iconBg, iconColor, proof, setProof }) => {
                          const uploading = proofUploading === key;
                          return (
                            <div
                              key={key}
                              className={`bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border shadow-sm flex flex-col gap-4 ${
                                proof ? 'border-green-200 dark:border-green-500/30' : 'border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                                  <Icon className={`w-[18px] h-[18px] ${iconColor}`} />
                                </div>
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                                  proof
                                    ? 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400'
                                    : 'bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400'
                                }`}>
                                  {proof ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                                  {proof ? 'Preuve soumise' : 'En attente'}
                                </span>
                              </div>

                              <div>
                                <p className="text-sm text-slate-400 mb-1">{label}</p>
                                <p className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight tabular-nums">
                                  {amount.toLocaleString('fr-FR')}
                                  <span className="text-sm font-medium text-slate-400 ml-1.5">FCFA</span>
                                </p>
                              </div>

                              {proof ? (
                                <div className="space-y-3 mt-auto">
                                  <a
                                    href={proof.proof_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-700 hover:opacity-90 transition-opacity"
                                  >
                                    <img src={proof.proof_url} alt={`Preuve — ${label}`} className="w-full h-32 object-cover" />
                                  </a>
                                  <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs text-slate-400 truncate">
                                      Envoyée le {new Date(proof.uploaded_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                                    </p>
                                    <a
                                      href={proof.proof_url}
                                      download
                                      className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0"
                                    >
                                      Télécharger
                                    </a>
                                  </div>
                                  <label className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm font-medium rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {uploading ? 'Envoi…' : 'Remplacer la preuve'}
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleProofUpload(key, setProof, e)} disabled={uploading} />
                                  </label>
                                </div>
                              ) : (
                                <div className="space-y-3 mt-auto">
                                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                                    Effectuez le paiement, puis chargez une capture d'écran comme preuve.
                                  </p>
                                  <label className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 dark:bg-blue-600 hover:bg-slate-800 dark:hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                    {uploading ? (
                                      <><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Envoi en cours…</>
                                    ) : (
                                      <><CreditCard className="w-4 h-4" /> Charger ma preuve</>
                                    )}
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handleProofUpload(key, setProof, e)} disabled={uploading} />
                                  </label>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Dossier + contact */}
                      <div className="bg-white dark:bg-slate-800 rounded-3xl p-5 sm:p-6 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col">
                        <div className="flex items-center justify-between gap-4 mb-5">
                          <div className="min-w-0">
                            <h2 className="text-lg font-semibold text-slate-900 dark:text-white tracking-tight mb-1">Mon dossier</h2>
                            <p className="text-xs text-slate-400">{proofsSubmitted} preuve{proofsSubmitted > 1 ? 's' : ''} sur 2</p>
                          </div>
                          <div className="relative w-16 h-16 flex-shrink-0">
                            <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                              <circle cx="32" cy="32" r="27" fill="none" strokeWidth="7" className="stroke-slate-100 dark:stroke-slate-700" />
                              <circle
                                cx="32" cy="32" r="27" fill="none" strokeWidth="7" strokeLinecap="round"
                                className="stroke-blue-600 transition-all duration-700"
                                strokeDasharray={`${(dossierPct / 100) * 169.6} 169.6`}
                              />
                            </svg>
                            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-900 dark:text-white tabular-nums">
                              {dossierPct}%
                            </span>
                          </div>
                        </div>

                        {/* Checklist */}
                        <div className="space-y-2">
                          {feeItems.map(({ key, label, proof }) => (
                            <div
                              key={key}
                              className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700"
                            >
                              {proof ? (
                                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                              ) : (
                                <Clock className="w-4 h-4 text-orange-500 flex-shrink-0" />
                              )}
                              <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{label}</span>
                            </div>
                          ))}
                          <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-slate-50/70 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700">
                            <BadgeCheck className="w-4 h-4 text-green-600 flex-shrink-0" />
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">Inscription validée</span>
                          </div>
                        </div>

                        {/* Contact administration */}
                        <div className="border-t border-slate-100 dark:border-slate-700 pt-5 mt-auto">
                          <p className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Une question ?</p>
                          <p className="text-xs text-slate-400 mb-3 leading-relaxed">
                            Contactez l'administration pour vos frais, votre statut ou votre dossier.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <a
                              href="mailto:gnonlonfoun@ensetmasters.org"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 px-3.5 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                              <EmailIcon /> Email
                            </a>
                            <a
                              href="https://wa.me/22901097565871"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 px-3.5 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            >
                              <WhatsAppIcon /> WhatsApp
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'profile' && (
                  <ProfileSection />
                )}

                {activeTab === 'settings' && (
                  <SettingsSection />
                )}

                {activeTab !== 'courses' && activeTab !== 'assignments' && activeTab !== 'scolarite' && activeTab !== 'profile' && activeTab !== 'settings' && (
                  <div className="bg-white rounded-2xl p-8 border border-slate-200">
                    <p className="text-slate-600">Contenu de l'onglet "{activeTab}" à développer...</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>

        {/* ── Modal soumission de devoir ── */}
        {isSubmissionModalOpen && selectedAssignment && (() => {
          const deadlineStatus = getGlobalDeadlineStatus();
          return (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className={`rounded-2xl shadow-2xl border w-full max-w-sm flex flex-col max-h-[90vh] ${
              deadlineStatus.isPast
                ? 'bg-slate-100 border-slate-300 opacity-90'
                : 'bg-white border-slate-200'
            }`}>

              {/* Header */}
              <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex items-start justify-between gap-3 flex-shrink-0">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold tracking-widest text-blue-500 uppercase mb-0.5">Rendre le devoir</p>
                  <h2 className="text-sm font-bold text-slate-900 leading-snug line-clamp-2">{selectedAssignment.course}</h2>
                </div>
                <button
                  onClick={closeSubmissionModal}
                  disabled={isSubmitting}
                  className="w-7 h-7 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center flex-shrink-0 disabled:opacity-30"
                >
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Deadline warning */}
              {globalSubmissionDeadline && (() => {
                const status = deadlineStatus;
                const deadlineDate = new Date(globalSubmissionDeadline);
                if (status.isPast) {
                  return (
                    <div className="px-5 py-3 bg-red-50 border-b border-red-200">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-red-700">Délai de soumission dépassé</p>
                          <p className="text-xs text-red-600">Vous ne pouvez plus soumettre aucun devoir. La date limite était le {deadlineDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    </div>
                  );
                } else if (status.isApproaching) {
                  return (
                    <div className="px-5 py-3 bg-amber-50 border-b border-amber-200">
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-amber-700">Deadline approche</p>
                          <p className="text-xs text-amber-600">{status.hoursLeft} heure{status.hoursLeft! > 1 ? 's' : ''} restante{status.hoursLeft! > 1 ? 's' : ''} pour soumettre. Deadline: {deadlineDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="px-5 py-3 bg-blue-50 border-b border-blue-200">
                      <div className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-blue-600">Deadline de soumission: <span className="font-semibold">{deadlineDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></p>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Contenu scrollable */}
              <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">

                {/* Message deadline passée */}
                {deadlineStatus.isPast && (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-red-700 mb-2">Délai de soumission dépassé</h3>
                    <p className="text-sm text-slate-600 mb-2">Vous ne pouvez plus soumettre aucun devoir.</p>
                    {globalSubmissionDeadline && (
                      <p className="text-xs text-slate-500">
                        La date limite était le {new Date(globalSubmissionDeadline).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                  </div>
                )}

                {/* Loader upload */}
                {!deadlineStatus.isPast && isSubmitting && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-700">
                        {uploadProgress < 10 ? 'Préparation…' : uploadProgress < 95 ? 'Envoi en cours…' : 'Finalisation…'}
                      </span>
                      <div className="flex items-center gap-2">
                        {uploadSpeed !== null && uploadProgress >= 10 && uploadProgress < 95 && (
                          <span className="text-slate-400 font-mono text-[11px]">
                            {uploadSpeed >= 1
                              ? `${uploadSpeed.toFixed(1)} Mo/s`
                              : `${(uploadSpeed * 1024).toFixed(0)} Ko/s`}
                          </span>
                        )}
                        <span className="font-bold text-blue-600">{uploadProgress}%</span>
                      </div>
                    </div>
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-slate-400 text-center">
                      Ne fermez pas cette fenêtre pendant l'envoi
                    </p>
                  </div>
                )}

                {/* Succès */}
                {!deadlineStatus.isPast && submissionSuccess && (
                  <div className="flex flex-col items-center gap-2 py-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-sm font-bold text-green-700">Devoir soumis !</p>
                    <p className="text-xs text-slate-400 text-center">Votre devoir a bien été enregistré.</p>
                  </div>
                )}

                {!deadlineStatus.isPast && !isSubmitting && !submissionSuccess && (
                  <>
                    {/* Zone fichier */}
                    <div>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept=".zip,application/zip,application/x-zip-compressed"
                        className="hidden"
                        id="assignment-file"
                      />
                      <label
                        htmlFor="assignment-file"
                        className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl border-2 border-dashed cursor-pointer transition-all duration-200 ${
                          submissionFile
                            ? 'border-blue-300 bg-blue-50'
                            : 'border-slate-200 bg-slate-50 hover:border-blue-300 hover:bg-blue-50/50'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${submissionFile ? 'bg-blue-600' : 'bg-slate-200'}`}>
                          <svg className={`w-4 h-4 ${submissionFile ? 'text-white' : 'text-slate-400'}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            {submissionFile
                              ? <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              : <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                            }
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          {submissionFile ? (
                            <>
                              <p className="text-xs font-semibold text-blue-700 truncate">{submissionFile.name}</p>
                              <p className="text-[11px] text-blue-500">{(submissionFile.size / 1024 / 1024).toFixed(1)} Mo · Cliquer pour changer</p>
                            </>
                          ) : (
                            <>
                              <p className="text-xs font-medium text-slate-600">Sélectionner l'archive ZIP</p>
                              <p className="text-[11px] text-slate-400">Fichier .zip uniquement</p>
                            </>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Titre */}
                    <input
                      type="text"
                      value={submissionTitle}
                      onChange={(e) => setSubmissionTitle(e.target.value)}
                      placeholder="Titre du devoir *"
                      className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 placeholder:text-slate-300 transition-all"
                    />

                    {/* Commentaires */}
                    <textarea
                      value={submissionComments}
                      onChange={(e) => setSubmissionComments(e.target.value)}
                      placeholder="Message pour l'enseignant (optionnel)"
                      className="w-full px-3.5 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50 placeholder:text-slate-300 resize-none transition-all"
                      rows={2}
                    />

                    {/* Note ZIP */}
                    <p className="text-[11px] text-slate-400 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20A10 10 0 0012 2z" />
                      </svg>
                      Compressez tous vos fichiers dans une archive .zip avant de soumettre.
                    </p>

                    {/* Erreur */}
                    {submissionError && (
                      <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{submissionError}</p>
                    )}
                  </>
                )}
              </div>

              {/* Footer boutons */}
              {!submissionSuccess && (
                <div className="px-5 pb-5 pt-3 border-t border-slate-100 flex gap-2.5 flex-shrink-0">
                  <button
                    onClick={closeSubmissionModal}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors disabled:opacity-40"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSubmissionSubmit}
                    disabled={!submissionFile || !submissionTitle.trim() || isSubmitting || deadlineStatus.isPast}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {deadlineStatus.isPast ? 'Délai dépassé' : isSubmitting ? 'Envoi…' : 'Soumettre'}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
        })()}

        {/* ── Lecteur PDF inline ── */}
        {pdfViewer && (
          <div className="fixed inset-0 z-50 flex flex-col bg-[#1e1e2e]">
            {/* Barre de titre */}
            <div className="flex items-center gap-3 px-4 h-13 bg-[#16162a] border-b border-white/10 flex-shrink-0" style={{ height: '52px' }}>
              <div className="w-8 h-8 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="flex-1 text-sm font-medium text-white/90 truncate">{pdfViewer.name}</p>
              <a
                href={pdfViewer.url}
                download
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition-colors flex-shrink-0"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Télécharger
              </a>
              <button
                onClick={() => setPdfViewer(null)}
                className="ml-1 p-1.5 rounded-lg hover:bg-white/10 transition-colors flex-shrink-0"
                title="Fermer"
              >
                <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Iframe */}
            <iframe
              src={`${pdfViewer.url}#toolbar=1&navpanes=1&scrollbar=1`}
              className="flex-1 w-full border-none"
              title={pdfViewer.name}
            />
          </div>
        )}

        {/* Modal des notifications */}
        {showNotifications && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                    <Bell className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Notifications</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">{notifications.length} message(s)</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowNotifications(false)} 
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {notifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-16 h-16 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
                  <p className="text-slate-500 dark:text-slate-400">Aucune notification pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {notifications.map(notification => (
                    <div 
                      key={notification.id} 
                      className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${
                        notification.is_read 
                          ? 'bg-slate-50 dark:bg-slate-700 border-slate-200 dark:border-slate-600' 
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600'
                      }`}
                      onClick={() => handleViewNotification(notification)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4 className="font-semibold text-slate-900 dark:text-white">
                              {notification.teacher_messages.message_title}
                            </h4>
                            {!notification.is_read && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
                                Nouveau
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                            Cours : {notification.teacher_messages.course_name}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            {new Date(notification.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <button className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal détail notification */}
        {showNotificationDetail && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 w-full max-w-2xl">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Message de l'enseignant</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {new Date(showNotificationDetail.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowNotificationDetail(null)} 
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-slate-50 dark:bg-slate-700 rounded-xl p-4">
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                    {showNotificationDetail.teacher_messages.message_title}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Cours : {showNotificationDetail.teacher_messages.course_name}
                  </p>
                </div>

                <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
                  <p className="text-slate-900 dark:text-white whitespace-pre-wrap">
                    {showNotificationDetail.teacher_messages.message_content}
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={() => setShowNotificationDetail(null)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}